// The email session (busy-my-web#99) — the state behind "Emails need attention".
//
// It owns only what the frames say the SESSION knows: whether it is open, which group is open
// inside it, that group's emails, which suggestion is armed, and how the archive is going. Two
// things it deliberately does NOT own:
//
//   • the sender groups — they are the Briefing's (`briefing/emailGroups`), because the Briefing
//     and the middle must be looking at the same inbox;
//   • the selection — that is the Work-board's, because Fatemeh's rule is that everything ticked
//     "enters the Work-board and the timeline", and a private copy here would be a second answer to
//     "what is selected" for the rail and the box to disagree over.
//
// The wording lives in lib/emailSession.js; this file is lifecycle only.
import api from '@/lib/services/general.service'
import { loginForSave } from '@/lib/services/guest'
import { archiveRequest, deleteRequest, withoutSenders } from '@/lib/emailWorkspace'
import { countsAfterWaitResolved, emailCounts, localDayKey } from '@/lib/briefings/email'
import { isCommerceEntry, reconcileCommerceCounts } from '@/lib/commerce'
// The open email's own create (busy-my-web#517): which form "Add" opens, and the event that opens it.
import { threadCreateMenu, THREAD_CREATE_EVENT } from '@/lib/emailThreadCreate'
import { withSystemGroupCount } from '@/lib/emailWorkspace'
// What a group HEADER's tick means for the list under it (busy-my-web#358).
import { groupTickPlan, titleTickPlan } from '@/lib/groupTickSelection'
// The viewer's own rules (busy-my-web#481): the record it holds, and what its zoom and rotate do.
import { ZOOM_REST, attachmentViewerFile, rotateRight, zoomIn, zoomOut } from '@/lib/attachmentViewer'
import {
  attachmentDocumentIds,
  attachmentPick,
  attachmentPicks,
  attachmentTimelineIds,
  savedToFilesNotice,
  savedToFilesReceipt,
} from '@/lib/emailAttachmentsListing'
import {
  addsOnCommit,
  archiveReceipt,
  archiveTargets,
  archivesOnCommit,
  deleteConfirmation,
  deleteReceipt,
  deleteTargets,
  deletesOnCommit,
  canCreate,
  createRequest,
  createTargets,
  emailPick,
  groupName,
  groupPick,
  groupPickId,
  sessionPicks,
  suggestedOngoingName,
  TEXT,
} from '@/lib/emailSession'
// The big-delete confirm a thread selection raises (busy-my-web#516).
import { threadsBinConfirmation } from '@/lib/deleteGuard'
import {
  canCreate as canCreateFromThreads,
  createRequest as threadsRequest,
  createSuggestionRunner,
  joinPlan,
  linkedNotice,
  pickedThreadIds,
  readSuggestion,
  selectionSignature,
  suggestRequest,
  threadTargets,
} from '@/lib/threadOngoingSuggestion'
import {
  addedToOngoingNotice,
  bulkReceipt,
  bulkRequest,
  bulkVerb,
  exportDocument,
  exportFilename,
  pickedTimelineIds,
} from '@/lib/emailThreadActions'
import { createGroupRequest, splitTitle } from '@/lib/emailGroups'
// The page-level "Ongoing Workspace" picker's own words and receipt (busy-my-web#421).
import {
  MATCH_TABLE_TAKE,
  connectReceipt,
  connectedNotice,
  connectingNotice,
  ongoingPickerRows,
  pickerControls,
} from '@/lib/ongoingPicker'
// A transient band the user must actually SEE (2026-09-15 parity audit, busy-my-web#494 (c) /
// #498): the local BFF answers in under 150ms, so "Archiving 733 emails..." was replaced by its own
// receipt before a frame was painted. The CALL is not slowed — only the moment the receipt replaces
// the band. A failure is not held back at all.
import { withMinimumBeat } from '@/lib/minimumBeat'
// The ONE category an archived set of groups all came from, which is what the receipt names
// ("Social media email groups", frame 40008242:423135 — busy-my-web#38's web half).
import { categoryOfSenders, categoryReceiptName } from '@/lib/emailCategories'
// The rail receipt a MATCH-accepted connection leaves (busy-my-web#496): `connectSuggestedGroups`
// wrote none at all, so a connection made from the chooser left the rail silent while the same
// connection made through the picker wrote one.
import { matchReceipt } from '@/lib/emailGroupMatches'
// The category layer's own reading of the summary (busy-main-bff#38).
import { readSummary, summaryRequest } from '@/lib/emailCategories'
// The subject families inside a group (busy-main-bff#39).
import { familiesRequest, familyPick, familyTimelineIds, readFamilies } from '@/lib/emailThreadFamilies'
// The "Possible Ongoing matches" panel (busy-main-bff#40).
import { matchRows, matchesRequest, panelCta, readExisting } from '@/lib/ongoingMatchesPanel'
// The listing's fourth column, and the ids a fetched group stands for (busy-my-web#370).
import {
  connectedMatchNotice,
  connectingMatchNotice,
  dismissedMatchNotice,
  groupTimelineIds,
  matchChipLabel,
  matchedGroupNames,
  matchedGroupPicks,
  matchedRelationshipIds,
} from '@/lib/emailGroupMatches'
// …the offers themselves, grouped once for the whole app (busy-my-web#317).
import { confidentMatches, matchCards } from '@/lib/possibleMatches'
import { withoutGroup } from '@/lib/emailBriefingGroups'
import { EMAIL_SESSION, workboardContext } from '@/lib/workboardContext'
// Every list behind the Email card pages through ALL of its rows (Saffet 2026-10-28).
import {
  appendPage,
  hasMore,
  readSenderThreadsPage,
  readThreadsPage,
  senderThreadsRequest,
} from '@/lib/emailListPaging'

/** How often the Briefing's counts are re-read while the session is open (busy-my-web#273). */
export const BRIEFING_POLL_MS = 60000
/** How many threads one entry lists at a time — the frame's page. "Show more" asks for the next one. */
export const ENTRY_PAGE_SIZE = 50
/** How many label / user groups one read of that listing asks for (the BFF's own per-call cap). */
export const ENTRY_GROUPS_PAGE_SIZE = 100
/** …and how many such reads at most: the listing is read whole, never left at its first page. */
export const ENTRY_GROUPS_MAX_PAGES = 10
// Module-scoped, like every other clock in the store (see globalTimeline.js): a timer is not state
// the UI renders, and keeping it out of `state` stops it being reactive for no reason.
let briefingTimer = null
// The debounced suggest call (busy-my-web#275). One runner for the module — the question it answers
// is "what is selected", of which there is one — created on first use so nothing schedules a timer
// at import time. `resetThreadSuggestionRunner` exists for tests and for the session's own RESET.
let runner = null
function threadRunner(dispatch) {
  if (!runner) {
    runner = createSuggestionRunner((ids, signature) =>
      dispatch('fetchThreadSuggestion', { ids, signature }))
  }
  return runner
}
export function resetThreadSuggestionRunner() {
  runner?.reset()
  runner = null
}

/**
 * Save a JSON document to the user's disk (busy-my-web#274, the Export action).
 *
 * The same six lines every other download in the app uses (Workboard's attachment download,
 * EntityAttachments) rather than a new helper: a blob, an anchor, a click, a revoke. Guarded on
 * `document` so importing this module in a test runner without a DOM does not throw at call time —
 * the action then reports the export as failed, which is true.
 */
function saveJson(name, payload) {
  if (typeof document === 'undefined' || typeof URL?.createObjectURL !== 'function') return false
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return true
}

/**
 * Save a downloaded blob to the user's disk (busy-my-web#423's "Download all").
 *
 * The same anchor `saveJson` above uses, and for the same reason it is guarded on `document`: the
 * store is imported by a test runner with no DOM, where the honest answer is "this did not save".
 */
function saveBlob(name, blob) {
  if (!blob) return false
  if (typeof document === 'undefined' || typeof URL?.createObjectURL !== 'function') return false
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return true
}

/**
 * Where stage 5 would copy the files (busy-my-web#423).
 *
 * LIVE since 2026-09-15 (parity audit D-5). It was named here while nothing answered it — the BFF's
 * document surface had upload, per-entity list, download and batch-list and no copy-into-my-Files
 * endpoint at all (checked 2026-09-08). busy-document-srv now has `POST v1/document/copy-to-files`
 * and busy-main-bff proxies it here, so the option that fires it (lib/emailAttachmentsListing's
 * `attachmentActions`) is `ready: true` and this path is the one the box calls.
 *
 * `POST document/save-to-files` `{ documentIds, timelineId|null }` → `{ documents, failures, savedCount }`;
 * `POST document/save-to-files/undo` `{ documentIds: <the SOURCE ids> }` → the number removed.
 */
export const SAVE_TO_FILES_PATH = 'document/save-to-files'

/** A summary line is somebody else's prose; it goes into a note's HTML body escaped, never raw. */
function noteEscape(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export const state = {
  // The session is open — the middle is the Workspace, the rail is the Email timeline, the Ongoing
  // list is empty. Not a route: the workspace opens IN PLACE (see briefing.js's openEmails note).
  active: false,
  // What the MAILBOX listing (Inbox and its folders) reports about itself — `{ page, total, folder }`
  // — so the box can say "All 100 conversations on this page are selected, Select all 124
  // conversation in inbox" (frame 40009853:294742). Null off the mailbox.
  listing: null,
  // The sender of the group opened inside the session (flow 2), or null at the group list.
  openSender: null,
  // That group's emails, and whether they are being read.
  groupEmails: [],
  groupLoading: false,
  // How many threads the open group holds (the server's `total`, equal to the group's row count),
  // the request that read them — so "Show more" asks the same question one page on — and whether
  // that next page is on the wire.
  groupTotal: 0,
  groupRequest: null,
  groupLoadingMore: false,
  // Which "What would you like to do?" option is armed — the filled radio in frame 10. Arming is
  // deliberately separate from doing: the frame shows the option chosen and the primary button
  // waiting, so an archive always takes two gestures.
  armed: null,
  // idle | archiving | archived | error, plus what was asked for and what came back.
  archiveState: 'idle',
  archiveRequested: 0,
  archiveResult: null,
  // The banner is dismissible (it carries an X in the frame).
  noticeDismissed: false,
  // The permanent archive receipts the rail keeps (frame 12), newest first.
  receipts: [],

  // ── DELETE, and undoing it (acc-account-sync-srv#18, 2026-09-15 parity audit) ──
  // idle | deleting | deleted | restoring | restored | error | restore-error, with what was asked
  // for and what came back — the archive's own shape, because the banner draws both.
  deleteState: 'idle',
  deleteRequested: 0,
  deleteResult: null,
  // The question the strip asks BEFORE anything is deleted (`deleteConfirmation`), or null. This is
  // the whole reason a delete takes three gestures where an archive takes two: mail that leaves the
  // mailbox should never be one press away.
  deleteConfirm: null,
  // The folder the workspace is scoped to, when it is scoped to one — sent as `mainFolder` on both
  // routes. NOTHING sets it today: the Email Workspace lists sender groups across the mailbox, not
  // a folder. It exists so the day a folder scope is added the request already carries it, and
  // `deleteRequest` leaves the field OFF the wire while it is null.
  mainFolder: null,

  // ── Create an ongoing (busy-my-web#104) ──
  // Which step of the create flow is showing: null while choosing, 'naming' on the "Create an
  // ongoing" panel (frames 6-7). A stage rather than a boolean because the CTA, the Briefing's
  // title and what the panel draws all read it.
  createStage: null,
  // The name in the field. Held here, not in the panel, because the "Creating ongoing, <name>"
  // banner outlives the panel that typed it.
  ongoingName: '',
  // idle | creating | created | error.
  createState: 'idle',
  // The story the server made, kept so the banner can name it and the caller can open it.
  createdOngoing: null,
  createNoticeDismissed: false,

  // ── The Briefing dashboard (busy-my-web#273) ──
  // The eight counts, as `GET v1/email/briefing` answered them, or null before it has. They live
  // with the SESSION rather than with the briefing's `remote` record because they are not written
  // about a particular input the way the AI sentence is — they are the state of the mailbox, and
  // they are re-read on a clock while the session is open.
  briefingCounts: null,
  // The user's local day the counts were read on ("2026-10-21", busy-my-web#668), so a reader that
  // only asks when it has none can tell yesterday's "N Emails today" from today's.
  briefingDay: '',
  briefingStatus: 'idle', // idle | loading | ready | error
  // The threads of the chosen entry, and how many there are in total.
  entryThreads: [],
  entryTotal: 0,
  entryLoading: false,
  // The request that read `entryThreads` (without its skip), so "Show more" asks for the NEXT page
  // of the same list — and a page that lands after the user moved to another list is dropped.
  entryRequest: null,
  entryLoadingMore: false,
  // ── The "N email groups" entry, opened (busy-main-bff#24) ──
  // The user's own groups as the BRIEFING listed them (with the sample subjects only that answer
  // carries), and which of them is open. Kept apart from `groups` below — that is the panel's own
  // read of the same collections, and the two answer different questions about them.
  entryGroups: [],
  openEntryGroup: null,
  // Which listing `entryGroups`/`openEntryGroup` currently hold: the user's own groups, or the
  // leftover label groups (2026-09-26) — same row shape (id/title/threadCount), reused rather than
  // duplicated, so this one flag is what tells `openBriefingGroup` which backend param to send.
  entryGroupsKind: 'groups',
  // The thread OPENED out of that list: `{ id, subject }`. Kept because the breadcrumb names it
  // ("Briefing ❯ 18 emails today ❯ ⟨Project kickoff agenda ✕⟩") from anywhere in the app — the
  // thread view is a page of its own, and the strip must be able to say where the user walked from
  // without asking the router what a subject is.
  openThread: null,
  // WHAT the open thread or sender group was opened FROM, when that was the possible-matches list
  // (busy-my-web#608, Saffet 2026-10-09: "I open a possible connection to view the email … I click
  // back it takes me back to the email inbox it should go back to the suggestions listed"):
  // `{ from: 'matches', what: 'thread' | 'group' }`, or null. Closing that thing forgets it.
  openedFrom: null,
  // ── The attachment VIEWER (busy-my-web#481, 2026-09-15 parity audit D-4) ──
  //
  // The file open in the middle, as `attachmentViewerFile` shapes it, or null. It lives on the
  // SESSION — beside `openThread`, which it is the exact twin of — for the same two reasons that
  // one does: the breadcrumb has to be able to name it from anywhere in the app without asking the
  // router what a file is called, and it has to go away when the session does.
  //
  // `attachmentView` is how the file is being LOOKED AT: the zoom percentage and the rotation the
  // toolbar is set to. It is here rather than inside the viewer component because the two halves of
  // that one control are drawn in two different places — the buttons are a row inside the SmartBox
  // and the picture they turn is in the middle — and a component-local ref would have left the box
  // pressing "+" at a middle that never heard it.
  openAttachment: null,
  attachmentView: { zoom: ZOOM_REST, rotation: 0 },
  // Which half of an open COMMERCE entry the middle is showing (busy-my-web#292): 'commerce' — the
  // orders/receipts/subscriptions list the entry now opens — or 'emails', the classification thread
  // list it was read from, kept as a second tab. Meaningless for every other entry, and reset with
  // each one opened, so a tab chosen under "27 orders" cannot survive into "9 require action".
  commerceTab: 'commerce',
  // ── Suggested Ongoing from the picked threads (busy-my-web#275) ──
  // The BFF's offer for the CURRENT selection, and the selection it was asked about — kept so a
  // late answer for a selection that has since changed is dropped rather than shown.
  threadSuggestion: null,
  threadSignature: '',
  // idle | loading | ready | error. The row is only drawn on `ready`; a failure is silent, because
  // an offer nobody asked for must not become an error message.
  threadSuggestionState: 'idle',
  // Which membership the create step is building: 'groups' (the senders, busy-my-web#104) or
  // 'threads' (the picks, #275). One naming step, two memberships — see `activeCreateTargets`.
  createMode: 'groups',
  // "3 threads linked to Insurance Renewal" — the receipt after a JOIN, which (unlike a create) does
  // not open a new story, so without this nothing on screen would say it happened.
  joinNotice: null,
  joining: false,
  // ── The Work-board's thread actions (busy-my-web#274) ──
  // One flag for "a thread action is in flight", not one per verb: only one can be armed at a time,
  // so per-verb flags would be six booleans of which five are always false, and the panel would have
  // to ask all six to know whether to disable its button.
  threadRunning: false,
  // The last thread action's outcome, as a banner: { kind, text }. Shares the notification slot the
  // archive already owns — one place where the session says what just happened.
  threadNotice: null,
  threadNoticeDismissed: false,
  // The summary callout (Summarise threads): the server's { summary, points, perThread, isFallback }.
  summary: null,
  // The task the threads imply, held while the composer opens prefilled — see createTaskDraft.
  taskDraft: null,
  // Which panel the armed option opened: null | 'ongoing' (the chooser) | 'group' (the naming step).
  threadPanel: null,
  // The chooser's query, and the user's ongoings as it last read them.
  ongoingQuery: '',
  // The group naming step's field.
  groupName: '',
  // The user's own email groups (the named collections, NOT the sender clusters).
  groups: [],
  groupsLoading: false,
  // Which threads the open panel is about, when that is NOT the board's picks.
  //
  // A group row's own "Add to ongoing" acts on the GROUP's threads — the user pointed at that row,
  // not at whatever happens to be ticked — so the chooser needs a target of its own. Null means the
  // ordinary case: the panel is about the selection.
  panelTimelineIds: null,

  // ── The page-level Ongoing picker (busy-my-web#421) ──
  // The frames' "search ongoins" flow does not ask "which Ongoing?" in the 260px strip — it turns
  // the MIDDLE into an "Ongoing Workspace" listing (lib/ongoingPicker.js). So the session records
  // that the middle is showing it, what is typed into it, and what the picker is ABOUT — the group
  // name the notice quotes ("Linkedin group is connecting to…"), which is empty when the picker was
  // opened from a bare selection with no group behind it.
  //
  // WHICH threads it will file is not stored twice: it is `panelTimelineIds`, the same slot the
  // inline chooser uses, so `addThreadsToOngoing` needs no second idea of what a panel is about.
  pickerOpen: false,
  pickerQuery: '',
  pickerLabel: '',
  // The row that is mid-connect, so its "Connect" can become the frame's pending dots without the
  // whole listing going disabled.
  pickerConnecting: null,
  // ── The picker's SEARCH (busy-my-web#535, frames "search ongoings 2–4") ──
  // `pickerQuery` is what the composer holds right now; a search runs only when "Search" is
  // pressed, and what it answered is kept here — the query it ran (the middle's "<query> result"
  // heading and the crumb), the rows, and whether it is still running (the CTA's three dots).
  pickerSearchQuery: '',
  pickerSearchRows: [],
  pickerSearchStatus: 'idle', // idle | searching | ready | error

  // ── The attachment selection's stage 5 (busy-my-web#423) ──
  // The files the last "Save to Files" filed, kept only so its Undo has something to name. Emptied
  // by the undo itself, because a receipt that has been reversed must not offer to reverse it again.
  savedFiles: [],

  // ── THE CATEGORY LAYER (2026-09-15 parity audit F1-1, busy-main-bff#38) ──
  // "14 email groups" is no longer one flat listing: the strip lists the CATEGORIES the sender
  // groups fall in (Social media · Shopping · Work & Tools · Finance · Health · Small groups ·
  // Other), each with a "View ↗" that narrows the middle to that category's groups. The server
  // categorises (`POST v1/email/groups/summary`); the web decides what to draw (lib/emailCategories).
  categories: [],
  categorySuggestions: [],
  smallGroupMax: 5,
  categoriesStatus: 'idle', // idle | loading | ready | error
  // Which category's sub-listing the middle is showing ('' at the full listing).
  openCategory: '',

  // ── SUBJECT FAMILIES inside a group — "5 Threads" (F1-3, busy-main-bff#39) ──
  // The open group's mail clustered by normalised subject, and whether that clustering IS the
  // middle. The families are read when a group opens, so the group's own rows can carry their
  // "LinkedIn 12" count without a second request.
  families: [],
  familiesStatus: 'idle', // idle | loading | ready | error
  familiesOpen: false,

  // ── "POSSIBLE ONGOING MATCHES" (F1-4, busy-main-bff#40) ──
  // The step between "Add to ongoing" and the page-level picker: three ranked EXISTING Ongoings as
  // radios, a search box, and a CTA that reads "Add" or "Search" (lib/ongoingMatchesPanel).
  matchesPanelOpen: false,
  matchesPanelRows: [],
  matchesPanelStatus: 'idle', // idle | loading | ready | error
  matchesPanelPicked: '',
  matchesPanelQuery: '',
  matchesPanelSearching: false,
}

/** One page of a Briefing list (`GET v1/email/briefing/threads`) as `{ rows, total }`. */
async function readBriefingThreads(params, skip) {
  const res = await api.get('email/briefing/threads', { ...params, skip, take: ENTRY_PAGE_SIZE })
  return readThreadsPage(res?.data?.data ?? res?.data ?? {})
}

/**
 * One page of a sender group's threads as `{ items, total }`. The exact route first; only where the
 * BFF does not have it yet (web deployed ahead of it) the old timeline search, so a group still
 * opens — that fallback is the fuzzy read this replaced, and goes when the BFF is out.
 */
async function readSenderThreads(request) {
  if (!request) return { items: [], total: 0 }
  try {
    const res = await api.insert('email/groups/sender-threads', request)
    return readSenderThreadsPage(res?.data?.data ?? res?.data)
  } catch (e) {
    if (request.skip > 0) throw e
    const term = request.emailAddress || request.sender
    const res = await api.get('timeline-search/listing', { search: term, page: 1, pageSize: ENTRY_PAGE_SIZE, order: 1 })
    const { items } = readSenderThreadsPage(res?.data?.data ?? res?.data)
    // The search's own total counts matches the group does not hold; only the rows are kept.
    return { items, total: items.length }
  }
}

/** The whole group listing of one Briefing entry, read a page at a time: `{ groups, total }`. */
async function readEntryGroupsWhole(entry) {
  const groups = []
  let total = 0
  for (let page = 0; page < ENTRY_GROUPS_MAX_PAGES; page++) {
    const res = await api.get('email/briefing/threads', { entry, skip: groups.length, take: ENTRY_GROUPS_PAGE_SIZE })
    const data = res?.data?.data ?? res?.data ?? {}
    const more = Array.isArray(data.groups) ? data.groups : []
    total = Number(data.total) || 0
    groups.push(...more)
    if (!more.length || groups.length >= total) break
  }
  return { groups, total }
}

export const getters = {
  active: s => s.active,
  listing: s => s.listing,
  openSender: s => s.openSender,
  // The open group itself, resolved against the Briefing's list so it carries the live count.
  openGroup: (s, g, rs, rootGetters) => {
    if (!s.openSender) return null
    const groups = rootGetters['briefing/emailGroups'] || []
    return groups.find(x => x && x.sender === s.openSender) || null
  },
  groupEmails: s => s.groupEmails,
  groupLoading: s => s.groupLoading,
  groupTotal: s => s.groupTotal,
  groupLoadingMore: s => s.groupLoadingMore,
  groupHasMore: s => !!s.groupRequest && hasMore(s.groupEmails, s.groupTotal),
  armed: s => s.armed,
  archiveState: s => s.archiveState,
  archiveRequested: s => s.archiveRequested,
  archiveResult: s => s.archiveResult,
  noticeDismissed: s => s.noticeDismissed,
  receipts: s => s.receipts,
  deleteState: s => s.deleteState,
  deleteRequested: s => s.deleteRequested,
  deleteResult: s => s.deleteResult,
  deleteConfirm: s => s.deleteConfirm,
  mainFolder: s => s.mainFolder,
  // ── The category layer / families / matches panel (2026-09-15 parity audit) ──
  categories: s => s.categories,
  categorySuggestions: s => s.categorySuggestions,
  smallGroupMax: s => s.smallGroupMax,
  categoriesStatus: s => s.categoriesStatus,
  openCategory: s => s.openCategory,
  families: s => s.families,
  familiesStatus: s => s.familiesStatus,
  familiesOpen: s => s.familiesOpen,
  matchesPanelOpen: s => s.matchesPanelOpen,
  matchesPanelRows: s => s.matchesPanelRows,
  matchesPanelStatus: s => s.matchesPanelStatus,
  matchesPanelPicked: s => s.matchesPanelPicked,
  matchesPanelQuery: s => s.matchesPanelQuery,
  matchesPanelSearching: s => s.matchesPanelSearching,
  /** What `commitControls` needs to know about the panel to word the CTA (lib/ongoingMatchesPanel). */
  addPanel: s => ({
    open: s.matchesPanelOpen,
    cta: panelCta({
      picked: s.matchesPanelPicked,
      query: s.matchesPanelQuery,
      searching: s.matchesPanelSearching,
    }),
  }),
  // What the session has ticked, read off the board.
  picks: (s, g, rs, rootGetters) => sessionPicks(rootGetters['workboard/items'] || []),
  isPicked: (s, g, rs, rootGetters) => id => (rootGetters['workboard/items'] || []).some(i => i.id === id),

  createStage: s => s.createStage,
  ongoingName: s => s.ongoingName,
  createState: s => s.createState,
  createdOngoing: s => s.createdOngoing,
  createNoticeDismissed: s => s.createNoticeDismissed,
  // The groups a create would connect — the picks, or the one open group. Derived, never stored:
  // unlinking one on the naming step is a Work-board removal, and this must follow it.
  /**
   * The dashboard's counts, with the three commerce rows stating what their LISTS hold
   * (busy-my-web#292). `GET v1/email/briefing` counts classified threads, and the row now opens an
   * aggregate list — so a row promising 27 and opening 9 is reconciled here, once, where every
   * reader of the counts (the strip, the crumb, the workspace's heading) picks it up together.
   */
  // (`rootGetters` is read defensively for the same reason every other cross-module getter here is
  // written to survive a store that does not hold the other module: a session without the commerce
  // lists states the counts it was given, which is what it did before this getter existed.)
  // …and `emailGroups` is the SENDER clusters' count (Figma 40009470-209128): the dashboard's own
  // number counts the user collections of bff#24, which the Briefing no longer lists. Same place,
  // same reason — every reader of the counts picks it up together.
  briefingCounts: (s, g, rs, rootGetters) =>
    withSystemGroupCount(
      reconcileCommerceCounts(s.briefingCounts, rootGetters?.['commerce/totals']),
      rootGetters?.['briefing/emailGroups'],
      // …but not the empty list `openEmails` declares while the clusters are still on the wire
      // (busy-my-web#607) — that is "not known yet", and its 0 must not hide the dashboard's count.
      // …stating the TRUE number of groups once the page route has said it (Saffet 2026-10-28).
      { loading: !!rootGetters?.['briefing/emailGroupsLoading'], total: rootGetters?.['briefing/emailGroupsTotal'] ?? null }
    ),
  briefingStatus: s => s.briefingStatus,
  briefingDay: s => s.briefingDay,
  commerceTab: s => s.commerceTab,
  entryThreads: s => s.entryThreads,
  entryTotal: s => s.entryTotal,
  entryLoading: s => s.entryLoading,
  entryLoadingMore: s => s.entryLoadingMore,
  entryHasMore: s => !!s.entryRequest && hasMore(s.entryThreads, s.entryTotal),
  openThread: s => s.openThread,
  openedFrom: s => s.openedFrom || null,
  // The viewer (busy-my-web#481): what is open, and how it is being looked at.
  openAttachment: s => s.openAttachment,
  attachmentView: s => s.attachmentView,

  /**
   * What the create step is about, whichever membership it is building: the picked groups
   * (busy-my-web#104) or the picked threads (busy-my-web#275). Every reader — the panel, the CTA,
   * the Briefing strip — asks this one getter, so none of them can be listing one thing while
   * another is sent. Derived, never stored: unlinking a row is a Work-board removal and all of them
   * must follow it.
   */
  createTargets: (s, g, rs, rootGetters) => (s.createMode === 'threads'
    ? threadTargets(rootGetters['workboard/items'] || [], s.threadSuggestion)
    : createTargets({
      openGroup: g.openGroup,
      items: rootGetters['workboard/items'] || [],
      groups: rootGetters['briefing/emailGroups'] || [],
    })),

  // ── busy-my-web#275 ──
  threadSuggestion: s => s.threadSuggestion,
  threadSuggestionState: s => s.threadSuggestionState,
  createMode: s => s.createMode,
  joinNotice: s => s.joinNotice,
  joining: s => s.joining,
  /** The email threads on the board — what the offer is asked about and what a create would hold. */
  threadPicks: (s, g, rs, rootGetters) => pickedThreadIds(rootGetters['workboard/items'] || []),
  /** The threads a create would link: the offer's own clump where there is one, else every pick. */
  threadCreateTargets: (s, g, rs, rootGetters) =>
    threadTargets(rootGetters['workboard/items'] || [], s.threadSuggestion),
  // ── The Work-board's thread actions (busy-my-web#274) ──
  threadRunning: s => s.threadRunning,
  threadNotice: s => (s.threadNoticeDismissed ? null : s.threadNotice),
  summary: s => s.summary,
  taskDraft: s => s.taskDraft,
  threadPanel: s => s.threadPanel,
  ongoingQuery: s => s.ongoingQuery,
  groupName: s => s.groupName,
  groups: s => s.groups,
  groupsLoading: s => s.groupsLoading,
  /** The groups the Briefing's entry listed, and the one opened out of it (busy-main-bff#24). */
  entryGroups: s => s.entryGroups,
  openEntryGroup: s => s.openEntryGroup,
  entryGroupsKind: s => s.entryGroupsKind,
  /** The threads ticked, as the timeline ids every thread endpoint takes. Derived off the board. */
  threadIds: (s, g, rs, rootGetters) => pickedTimelineIds(rootGetters['workboard/items'] || []),
  /**
   * The threads an open panel is actually about: the group row's, when one pointed the panel at
   * itself, and the board's picks otherwise. Every panel action reads THIS rather than `threadIds`,
   * so "add to ongoing" from a group row cannot quietly act on the selection instead.
   */
  panelThreadIds: (s, g) => (s.panelTimelineIds?.length ? [...s.panelTimelineIds] : g.threadIds),

  // ── The page-level Ongoing picker (busy-my-web#421) ──
  ongoingPickerOpen: s => s.pickerOpen,
  ongoingPickerQuery: s => s.pickerQuery,
  ongoingPickerLabel: s => s.pickerLabel,
  ongoingPickerConnecting: s => s.pickerConnecting,
  ongoingPickerSearch: s => ({ query: s.pickerSearchQuery, rows: s.pickerSearchRows, status: s.pickerSearchStatus }),
  /** The action row while the picker is up (lib/ongoingPicker's `pickerControls`), or null. */
  ongoingPickerControls: s => (s.pickerOpen
    ? pickerControls({ draft: s.pickerQuery, searched: s.pickerSearchQuery, searching: s.pickerSearchStatus === 'searching' })
    : null),
  /** What the last "Save to Files" filed — what its Undo would take back (busy-my-web#423). */
  savedFiles: s => s.savedFiles,
}

export const mutations = {
  SET_ACTIVE(s, v) { s.active = !!v },
  SET_LISTING(s, v) { s.listing = v && v.page != null ? { page: Number(v.page) || 0, total: Number(v.total) || 0, folder: String(v.folder || 'inbox') } : null },
  SET_OPEN_SENDER(s, v) { s.openSender = v || null },
  SET_GROUP_EMAILS(s, v) { s.groupEmails = Array.isArray(v) ? v : [] },
  SET_GROUP_LOADING(s, v) { s.groupLoading = !!v },
  SET_GROUP_PAGE(s, { total, request }) {
    s.groupTotal = Math.max(0, Number(total) || 0)
    s.groupRequest = request || null
  },
  APPEND_GROUP_EMAILS(s, more) { s.groupEmails = appendPage(s.groupEmails, more) },
  SET_GROUP_LOADING_MORE(s, v) { s.groupLoadingMore = !!v },
  SET_ARMED(s, v) { s.armed = v || null },
  SET_ARCHIVE(s, { status, requested, result }) {
    s.archiveState = status
    if (requested != null) s.archiveRequested = requested
    if (result !== undefined) s.archiveResult = result
    s.noticeDismissed = false
  },
  SET_DELETE(s, { status, requested, result }) {
    s.deleteState = status
    if (requested != null) s.deleteRequested = requested
    if (result !== undefined) s.deleteResult = result
    s.noticeDismissed = false
  },
  SET_DELETE_CONFIRM(s, v) { s.deleteConfirm = v || null },
  SET_MAIN_FOLDER(s, v) { s.mainFolder = (v === null || v === undefined || v === '') ? null : Number(v) },
  DISMISS_NOTICE(s) { s.noticeDismissed = true },
  ADD_RECEIPT(s, receipt) { s.receipts = [receipt, ...s.receipts] },
  SET_CATEGORIES(s, { categories = [], suggestions = [], smallGroupMax = 5, status = 'ready' } = {}) {
    s.categories = categories
    s.categorySuggestions = suggestions
    s.smallGroupMax = smallGroupMax
    s.categoriesStatus = status
  },
  SET_CATEGORIES_STATUS(s, status) { s.categoriesStatus = status },
  SET_OPEN_CATEGORY(s, category) { s.openCategory = String(category || '') },
  SET_FAMILIES(s, families) { s.families = families || [] },
  SET_FAMILIES_STATUS(s, status) { s.familiesStatus = status },
  SET_FAMILIES_OPEN(s, open) { s.familiesOpen = !!open },
  SET_MATCHES_PANEL(s, { open = false, rows = [], status = 'idle' } = {}) {
    s.matchesPanelOpen = !!open
    s.matchesPanelRows = rows
    s.matchesPanelStatus = status
    if (!open) { s.matchesPanelPicked = ''; s.matchesPanelQuery = ''; s.matchesPanelSearching = false }
  },
  SET_MATCHES_PANEL_ROWS(s, { rows = [], status = 'ready' } = {}) { s.matchesPanelRows = rows; s.matchesPanelStatus = status },
  SET_MATCHES_PANEL_PICKED(s, id) { s.matchesPanelPicked = String(id || '') },
  SET_MATCHES_PANEL_QUERY(s, q) { s.matchesPanelQuery = String(q ?? '') },
  SET_MATCHES_PANEL_SEARCHING(s, on) { s.matchesPanelSearching = !!on },
  SET_CREATE_STAGE(s, v) { s.createStage = v || null },
  SET_ONGOING_NAME(s, v) { s.ongoingName = v == null ? '' : String(v) },
  SET_CREATE(s, { status, ongoing }) {
    s.createState = status
    if (ongoing !== undefined) s.createdOngoing = ongoing
    s.createNoticeDismissed = false
  },
  DISMISS_CREATE_NOTICE(s) { s.createNoticeDismissed = true },
  SET_BRIEFING_COUNTS(s, { status, counts, day }) {
    s.briefingStatus = status
    // A failed re-read keeps the numbers it has: the counts on screen were true when they were
    // fetched, and blanking the dashboard because one poll missed says less than they do.
    if (counts !== undefined) s.briefingCounts = counts
    // Only a fresh SERVER read says which day the counts are about (busy-my-web#668): a local
    // adjustment of yesterday's numbers (a wait resolved) is still yesterday's, and must not make
    // Home stop re-asking.
    if (day !== undefined) s.briefingDay = String(day || '')
  },
  SET_ENTRY_THREADS(s, { threads, total }) {
    s.entryThreads = Array.isArray(threads) ? threads : []
    s.entryTotal = Number(total) || 0
  },
  SET_ENTRY_LOADING(s, v) { s.entryLoading = !!v },
  SET_ENTRY_REQUEST(s, v) { s.entryRequest = v || null },
  APPEND_ENTRY_THREADS(s, { threads, total }) {
    s.entryThreads = appendPage(s.entryThreads, threads)
    if (total != null) s.entryTotal = Number(total) || 0
  },
  SET_ENTRY_LOADING_MORE(s, v) { s.entryLoadingMore = !!v },
  SET_ENTRY_GROUPS(s, v) { s.entryGroups = Array.isArray(v) ? v : [] },
  SET_OPEN_ENTRY_GROUP(s, v) { s.openEntryGroup = v?.id ? { id: v.id, title: String(v.title || '') } : null },
  SET_ENTRY_GROUPS_KIND(s, v) { s.entryGroupsKind = v === 'labelGroups' ? 'labelGroups' : 'groups' },
  SET_OPEN_THREAD(s, v) {
    const before = s.openThread
    s.openThread = v || null
    // A thread opened from the matches list stops being "from the matches" once it is closed — or
    // once a DIFFERENT thread takes its place (#608 tester, 2026-10-09: B opened over A kept A's
    // origin, so B's Close went back to the matches list). PossibleMatchCards marks the origin
    // AFTER opening, so a thread that genuinely came from the list is re-marked right after this.
    const id = x => (x && (x.id || x.timelineId || x.threadId)) || x || null
    const replaced = !!v && !!before && id(v) !== id(before)
    if ((!v || replaced) && s.openedFrom?.what === 'thread') s.openedFrom = null
  },
  SET_OPENED_FROM(s, v) { s.openedFrom = v || null },
  /**
   * Open a file in the viewer, or close it (busy-my-web#481).
   *
   * Opening RESETS the zoom and the rotation. A file is a new subject, and carrying 200% and a
   * quarter turn from the last picture into the next one would have the viewer open on a file the
   * user has never seen, already wrong.
   */
  SET_OPEN_ATTACHMENT(s, v) {
    s.openAttachment = v?.id ? v : null
    s.attachmentView = { zoom: ZOOM_REST, rotation: 0 }
  },
  SET_ATTACHMENT_VIEW(s, v) { s.attachmentView = { ...s.attachmentView, ...(v || {}) } },
  /** 'emails' or the list. Anything unrecognised is the list, which is what an entry opens on. */
  SET_COMMERCE_TAB(s, v) { s.commerceTab = v === 'emails' ? 'emails' : 'commerce' },
  SET_THREAD_SUGGESTION(s, { suggestion, signature, status }) {
    if (suggestion !== undefined) s.threadSuggestion = suggestion
    if (signature !== undefined) s.threadSignature = signature
    s.threadSuggestionState = status
  },
  SET_CREATE_MODE(s, v) { s.createMode = v === 'threads' ? 'threads' : 'groups' },
  SET_JOIN_NOTICE(s, v) { s.joinNotice = v || null },
  SET_JOINING(s, v) { s.joining = !!v },

  // ── The Work-board's thread actions (busy-my-web#274) ──
  SET_THREAD_RUNNING(s, v) { s.threadRunning = !!v },
  SET_THREAD_NOTICE(s, notice) {
    s.threadNotice = notice || null
    // Every new notice arrives undismissed: the previous banner's × dismissed THAT sentence, and a
    // fresh outcome the user has not seen must not inherit its silence.
    s.threadNoticeDismissed = false
  },
  DISMISS_THREAD_NOTICE(s) { s.threadNoticeDismissed = true },
  SET_SUMMARY(s, v) { s.summary = v || null },
  SET_TASK_DRAFT(s, v) { s.taskDraft = v || null },
  SET_THREAD_PANEL(s, v) { s.threadPanel = v || null },
  SET_PANEL_THREADS(s, ids) { s.panelTimelineIds = Array.isArray(ids) && ids.length ? [...ids] : null },
  // ── The page-level Ongoing picker (busy-my-web#421) ──
  SET_PICKER(s, { open, label } = {}) {
    s.pickerOpen = !!open
    s.pickerLabel = open ? String(label || '') : ''
    if (!open) {
      s.pickerQuery = ''
      s.pickerConnecting = null
      s.pickerSearchQuery = ''
      s.pickerSearchRows = []
      s.pickerSearchStatus = 'idle'
    }
  },
  SET_PICKER_SEARCH(s, { query = '', rows = [], status = 'idle' } = {}) {
    s.pickerSearchQuery = String(query || '')
    s.pickerSearchRows = Array.isArray(rows) ? rows : []
    s.pickerSearchStatus = status
  },
  /** "Not related" on a match row: off the table for this session (busy-my-web#535). */
  DROP_MATCH_ROW(s, id) { s.matchesPanelRows = s.matchesPanelRows.filter(r => r && r.id !== id) },
  SET_PICKER_QUERY(s, v) { s.pickerQuery = v == null ? '' : String(v) },
  SET_PICKER_CONNECTING(s, v) { s.pickerConnecting = v || null },
  SET_SAVED_FILES(s, v) { s.savedFiles = Array.isArray(v) ? [...v] : [] },
  SET_ONGOING_QUERY(s, v) { s.ongoingQuery = v == null ? '' : String(v) },
  SET_GROUP_NAME(s, v) { s.groupName = v == null ? '' : String(v) },
  SET_GROUPS(s, v) { s.groups = Array.isArray(v) ? v : [] },
  SET_GROUPS_LOADING(s, v) { s.groupsLoading = !!v },
  /** One group changed — replace it in place, or add it if it is new, without re-reading the list. */
  UPSERT_GROUP(s, group) {
    if (!group?.id) return
    const at = s.groups.findIndex(g => g && g.id === group.id)
    s.groups = at === -1 ? [group, ...s.groups] : s.groups.map((g, i) => (i === at ? group : g))
  },
  /**
   * Leaving the session. `keepNotice` survives ONE thing on purpose: the "Ongoing created" banner.
   * Creating an ongoing closes the session (the board hands back, the picks go, the story opens),
   * and a receipt that is wiped by the very act it is reporting would never be seen.
   */
  RESET(s, { keepNotice = false } = {}) {
    s.active = false
    s.openSender = null
    s.groupEmails = []
    s.groupLoading = false
    s.groupTotal = 0
    s.groupRequest = null
    s.groupLoadingMore = false
    s.armed = null
    s.archiveState = 'idle'
    s.archiveRequested = 0
    s.archiveResult = null
    s.noticeDismissed = false
    s.receipts = []
    s.deleteState = 'idle'
    s.deleteRequested = 0
    s.deleteResult = null
    s.deleteConfirm = null
    s.createStage = null
    s.briefingCounts = null
    s.briefingStatus = 'idle'
    s.entryThreads = []
    s.entryTotal = 0
    s.entryLoading = false
    s.entryRequest = null
    s.entryLoadingMore = false
    s.entryGroups = []
    s.openEntryGroup = null
    s.openThread = null
    s.openedFrom = null
    // The viewer goes with the session too (busy-my-web#481): the file was opened out of a listing
    // that is now gone, so leaving it up would be a middle with no path behind it.
    s.openAttachment = null
    s.attachmentView = { zoom: ZOOM_REST, rotation: 0 }
    s.commerceTab = 'commerce'
    // The offer is about a selection, and the selection is gone with the session.
    s.threadSuggestion = null
    s.threadSignature = ''
    s.threadSuggestionState = 'idle'
    s.createMode = 'groups'
    s.joinNotice = null
    s.joining = false
    resetThreadSuggestionRunner()
    // The thread journey's own state goes with the session, all of it: a summary of threads that are
    // no longer selected, or a half-open ongoing chooser, would be an answer to a question nobody is
    // asking any more. The groups list goes too — it is re-read on the way in.
    s.threadRunning = false
    s.threadNotice = null
    s.threadNoticeDismissed = false
    s.summary = null
    s.taskDraft = null
    s.threadPanel = null
    s.panelTimelineIds = null
    s.pickerOpen = false
    s.pickerQuery = ''
    s.pickerLabel = ''
    s.pickerConnecting = null
    s.savedFiles = []
    s.ongoingQuery = ''
    s.groupName = ''
    s.groups = []
    s.groupsLoading = false
    if (!keepNotice) {
      s.ongoingName = ''
      s.createState = 'idle'
      s.createdOngoing = null
      s.createNoticeDismissed = false
    }
  },
}

export const actions = {
  /**
   * Enter the session: the Briefing reads the inbox, the board changes hands.
   *
   * The board's context flips to `email-session` WITHOUT a navigation (the workspace opens in
   * place), which is exactly what the context rule is for — leaving the session drops what was
   * ticked in it, and the research picks it displaced are not silently mixed in with the emails.
   */
  async open({ commit, dispatch }) {
    commit('SET_ACTIVE', true)
    dispatch('workboard/enterContext', EMAIL_SESSION, { root: true })
    await dispatch('briefing/openEmails', null, { root: true })
  },

  /** The header's close button: back to whatever page we were on, with nothing left behind. */
  close({ commit, dispatch }, { path = '', keepNotice = false } = {}) {
    commit('RESET', { keepNotice })
    dispatch('briefing/clearEmailGroups', null, { root: true })
    dispatch('workboard/enterContext', workboardContext(path), { root: true })
  },

  /** Ticking a group — straight onto the Work-board, which the rail and the Briefing read. */
  toggleGroup({ dispatch }, group) {
    if (!group) return
    dispatch('workboard/toggle', groupPick(group), { root: true })
  },

  /** Ticking one email inside an open group. */
  toggleEmail({ dispatch, getters: g }, email) {
    if (!email?.id) return
    dispatch('workboard/toggle', emailPick(email, g.openGroup), { root: true })
  },

  // ── A group HEADER's tick (busy-my-web#358) ───────────────────────────────────────────────────
  //
  // Saffet: "Like Recipe overview it i tick it the other 4 also gets ticked." One gesture, the
  // whole list under the header — and the picks it makes are exactly the picks the rows make, so
  // archive, "Create ongoing" and the rail go on reading ONE selection whichever way it was built.
  // The rows are handed in by the listing rather than re-derived here: the listing is what decides
  // which rows a header is the header of (an open group's mail, an entry's threads), and a second
  // opinion about that in the store is a second thing to keep in step.
  //
  // Whether the click fills or empties is lib/groupTickSelection.js's answer, not this action's —
  // three listings ask the same question and there is one place that answers it.

  /**
   * The header over an open group's EMAILS, or over a Briefing entry's threads.
   *
   * @param {Array<object>} emails the listing's rows, in the shape `toggleEmail` takes
   */
  toggleEmailList({ dispatch, getters: g, rootGetters }, emails) {
    const rows = (emails || []).filter(e => e && e.id)
    if (!rows.length) return
    const plan = groupTickPlan(
      rows.map(e => emailPick(e, e.group || g.openGroup)),
      rootGetters['workboard/items'] || [],
    )
    for (const item of plan.pick) dispatch('workboard/add', item, { root: true })
    for (const id of plan.drop) dispatch('workboard/remove', id, { root: true })
  },

  /**
   * The header over the sender listing — every group on it, or none.
   *
   * A group's pick already stands for all of that sender's mail (its `count` is the group's total,
   * which is what every "N Emails" line adds up), so picking the groups IS picking their emails:
   * the rows below a sender header are not fetched until it is opened, and a header that quietly
   * loaded five listings to tick them would be a page-load per click.
   *
   * @param {Array<object>} groups the group records the listing is showing
   */
  toggleGroupList({ dispatch, rootGetters }, groups) {
    const list = (groups || []).filter(Boolean)
    if (!list.length) return
    const plan = groupTickPlan(list.map(groupPick), rootGetters['workboard/items'] || [])
    for (const item of plan.pick) dispatch('workboard/add', item, { root: true })
    for (const id of plan.drop) dispatch('workboard/remove', id, { root: true })
  },

  // ── The attachments listing's ticks (busy-my-web#403) ───────────────────────────────────────
  // A file is picked onto the same Work-board the groups and the emails are, in its own namespace
  // (`attachment:`), so one selection is still one place — see lib/emailAttachmentsListing.js.

  /** Ticking one attachment in the workspace's attachments listing. */
  toggleAttachment({ dispatch }, row) {
    if (!row?.id) return
    dispatch('workboard/toggle', attachmentPick(row), { root: true })
  },

  /**
   * The attachments listing's header — every file on it, or none. Same fill-or-empty rule as the
   * other two headers (lib/groupTickSelection.js).
   *
   * @param {Array<object>} rows the listing's rows
   */
  toggleAttachmentList({ dispatch, rootGetters }, rows) {
    const list = (rows || []).filter(r => r && r.id)
    if (!list.length) return
    const plan = groupTickPlan(list.map(attachmentPick), rootGetters['workboard/items'] || [])
    for (const item of plan.pick) dispatch('workboard/add', item, { root: true })
    for (const id of plan.drop) dispatch('workboard/remove', id, { root: true })
  },

  /**
   * The PAGE TITLE's box (busy-my-web#401) — everything the title heads, or nothing.
   *
   * The listing hands in the rows it is SHOWING, in the two shapes the two lists have, because the
   * title heads whichever of them is open; the store does not re-derive that. Fill-or-empty is
   * lib/groupTickSelection.js's `titleTickPlan`, which differs from a group header's on purpose:
   * once anything under the title is picked the box wears the minus and clears the lot.
   *
   * @param {{groups?: Array<object>, emails?: Array<object>, attachments?: Array<object>}} payload
   *        the rows under the title
   */
  toggleTitleSelection({ dispatch, getters: g, rootGetters }, payload) {
    const groups = (payload?.groups || []).filter(Boolean)
    const emails = (payload?.emails || []).filter(e => e && e.id)
    const attachments = (payload?.attachments || []).filter(a => a && a.id)
    const picks = [
      ...groups.map(groupPick),
      ...emails.map(e => emailPick(e, e.group || g.openGroup)),
      ...attachments.map(attachmentPick),
    ]
    const plan = titleTickPlan(picks, rootGetters['workboard/items'] || [])
    for (const item of plan.pick) dispatch('workboard/add', item, { root: true })
    for (const id of plan.drop) dispatch('workboard/remove', id, { root: true })
  },

  /** "Clear selection" / "Clear selections" — the session's picks only; research items stay.
   *
   * FILES are session picks too (busy-my-web#482): the rail, the board and the action bar all offer
   * to clear a selection of attachments, and a clear that left them ticked would be the three
   * surfaces disagreeing about what "selected" means.
   */
  clearSelection({ dispatch, rootGetters, commit }) {
    const items = rootGetters['workboard/items'] || []
    for (const p of [...sessionPicks(items), ...attachmentPicks(items)]) {
      dispatch('workboard/remove', p.id, { root: true })
    }
    commit('SET_ARMED', null)
    // Nothing is selected, so there is nothing the offer could be about (busy-my-web#275).
    commit('SET_THREAD_SUGGESTION', { suggestion: null, signature: '', status: 'idle' })
    resetThreadSuggestionRunner()
  },

  /**
   * Open a group (flow 2). The rows are EXACTLY the threads that group's address sent
   * (`POST v1/email/groups/sender-threads`, Saffet 2026-10-28: "the groups in them are all mixed
   * up"). They used to come from the timeline listing SEARCHED by the sender, which matches a name
   * or address in any role and falls back to fuzzy matching — other senders' mail leaked in. The
   * server's `total` is the group's own row count, and "Show more" pages through the rest.
   */
  async openGroup({ commit, dispatch }, group) {
    if (!group?.sender) return
    commit('SET_OPEN_SENDER', group.sender)
    // A group is a LIST, and the middle can only show one thing: since busy-my-web#322 an open
    // thread is read in that middle, so opening a group has to close it — otherwise the workspace
    // goes on drawing the conversation while the crumb says the user is in a sender's mail.
    commit('SET_OPEN_THREAD', null)
    commit('SET_ARMED', null)
    commit('SET_GROUP_EMAILS', [])
    commit('SET_GROUP_PAGE', { total: 0, request: null })
    commit('SET_GROUP_LOADING', true)
    try {
      const request = senderThreadsRequest(group, 0, ENTRY_PAGE_SIZE)
      const { items, total } = await readSenderThreads(request)
      commit('SET_GROUP_EMAILS', items)
      commit('SET_GROUP_PAGE', { total, request })
    } catch {
      commit('SET_GROUP_EMAILS', [])
    } finally {
      commit('SET_GROUP_LOADING', false)
    }
    // …and the group's subject FAMILIES (2026-09-15 parity audit F1-3): the "N Threads" row and the
    // per-row "LinkedIn 12" count both need them, and asking once here is cheaper than twice later.
    // Its own failure is silent — a group without a clustering is still a group.
    commit('SET_FAMILIES_OPEN', false)
    dispatch('loadFamilies', group)
  },

  /** The open group's next page of threads, appended — dropped if the user opened another group meanwhile. */
  async loadMoreGroupEmails({ commit, state: s }) {
    const request = s.groupRequest
    if (!request || s.groupLoading || s.groupLoadingMore || !hasMore(s.groupEmails, s.groupTotal)) return
    commit('SET_GROUP_LOADING_MORE', true)
    try {
      const before = s.groupEmails.length
      const { items, total } = await readSenderThreads({ ...request, skip: before })
      if (s.groupRequest !== request) return
      commit('APPEND_GROUP_EMAILS', items)
      // …and a page that added nothing ends it (see loadMoreEntryThreads).
      commit('SET_GROUP_PAGE', { total: s.groupEmails.length === before ? before : total, request })
    } catch {
      // nothing: the rows on screen stand, and "Show more" can be pressed again
    } finally {
      commit('SET_GROUP_LOADING_MORE', false)
    }
  },

  // ── The Briefing dashboard (busy-my-web#273) ──────────────────────────────────────────────────

  /**
   * The eight counts, in ONE call (`GET v1/email/briefing`).
   *
   * Guests are not asked at all: the endpoint is the signed-in user's mailbox, and a guest keeps
   * exactly the briefing they had before this feature — the two rows the client can work out from
   * the sender groups it holds (see lib/briefings/email.js's fallback).
   *
   * A refusal keeps whatever numbers are already on screen: they were true when they arrived, and
   * a dashboard that empties itself on one bad poll is less honest than one that is a minute old.
   */
  async loadBriefing({ commit, rootGetters, state: s }) {
    if (!rootGetters['user/isAuthenticated']) return
    if (s.briefingStatus === 'loading') return
    commit('SET_BRIEFING_COUNTS', { status: 'loading' })
    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
      const res = await api.get('email/briefing', { timeZoneId })
      // Only an answer that actually NAMES the entries is a dashboard (lib/briefings/email.js).
      // Anything else is a leg that did not land, and is recorded as such: the strip then keeps the
      // two rows it can work out from the sender groups instead of stating nothing at all.
      const counts = emailCounts(res?.data?.data ?? res?.data)
      commit('SET_BRIEFING_COUNTS', counts ? { status: 'ready', counts, day: localDayKey() } : { status: 'error' })
    } catch {
      commit('SET_BRIEFING_COUNTS', { status: 'error' })
    }
  },

  /**
   * Keep the counts live while the workspace is on screen — the same 60s the home page's other
   * standing panels refresh on. Idempotent: mounting the workspace twice starts one clock.
   *
   * Started by the view rather than by `open`, the way globalTimeline's clock is: a timer belongs to
   * something being LOOKED AT, and a session that outlives the column it was opened in (reading one
   * thread, say) must not go on polling from behind it.
   */
  startBriefingPoll({ dispatch }) {
    if (briefingTimer) return
    briefingTimer = setInterval(() => dispatch('loadBriefing'), BRIEFING_POLL_MS)
  },

  stopBriefingPoll() {
    if (briefingTimer) clearInterval(briefingTimer)
    briefingTimer = null
  },

  /**
   * An entry chosen in the Briefing — the middle lists that entry's threads.
   *
   * "Email groups" is the one entry that is not a list of threads (busy-main-bff#24): it is the
   * user's own GROUPS, so the middle lists those and a group is opened to reach its threads. Until
   * that issue the number named the sender clusters and this cleared the list, which left the one
   * entry on the dashboard that could be counted and not opened.
   */
  async openEntry({ commit, dispatch }, entry) {
    commit('SET_OPEN_THREAD', null)
    commit('SET_ARMED', null)
    // Every entry opens on its own first face: for the three commerce entries that is the LIST,
    // never whichever tab the last one was left on (busy-my-web#292).
    commit('SET_COMMERCE_TAB', 'commerce')
    commit('SET_OPEN_ENTRY_GROUP', null)
    commit('SET_ENTRY_REQUEST', null)
    if (!entry) {
      commit('SET_ENTRY_THREADS', { threads: [], total: 0 })
      commit('SET_ENTRY_GROUPS', [])
      return
    }
    // Leaving a sender group behind: an entry is a view over the whole inbox, not inside a group.
    commit('SET_OPEN_SENDER', null)
    commit('SET_GROUP_EMAILS', [])
    // The one entry that is not a list of threads: "N email groups" opens the SENDER listing the
    // workspace already holds (Figma 40009470-209128, design clarification 2026-09-02 — groups are
    // system-made clusters, not the user collections bff#24 listed here). Nothing to fetch: the
    // clusters are the Briefing's own groups, and `loadEntryGroups` stays for the panel's verbs.
    if (entry === 'groups' || entry === 'labelGroups') {
      commit('SET_ENTRY_GROUPS_KIND', entry)
      commit('SET_ENTRY_THREADS', { threads: [], total: 0 })
      commit('SET_ENTRY_GROUPS', [])
      await dispatch('loadEntryGroups')
      return
    }
    commit('SET_ENTRY_GROUPS', [])
    // A commerce entry opens BOTH halves at once: the list it now shows (`commerce/ensure`, which
    // is a no-op once that list is in hand) and the threads behind its second tab, so switching
    // tabs is a switch rather than a wait.
    if (isCommerceEntry(entry)) dispatch('commerce/ensure', entry, { root: true })
    await dispatch('loadEntryThreads', entry)
  },

  /** The tab strip inside an open commerce entry — the list, or the emails it was read from. */
  setCommerceTab({ commit }, tab) { commit('SET_COMMERCE_TAB', tab) },

  /**
   * The groups behind "14 email groups" (busy-main-bff#24). The panel's own read goes out beside it:
   * the listing answers what the centre DRAWS (titles, sizes, sample subjects) and the group records
   * carry the memberships the #274 actions act on, so the verbs under a row are the same verbs.
   */
  async loadEntryGroups({ commit, dispatch, state: s }) {
    const labelKind = s.entryGroupsKind === 'labelGroups'
    commit('SET_ENTRY_LOADING', true)
    try {
      // The WHOLE listing, a page at a time (Saffet 2026-10-28): the card counts every group, so the
      // listing under it must hold every group, not the first page of them.
      const data = await readEntryGroupsWhole(labelKind ? 'labelGroups' : 'groups')
      // Label groups come back as { label, threadCount, lastReceivedUtc } — normalised to the same
      // { id, title, threadCount, updatedUtc } shape the user-groups listing uses, since every panel
      // that draws entryGroups reads it by those field names either way. The label itself is the id:
      // labels are unique per user, so it is as stable a key as a real Guid would be.
      const groups = labelKind
        ? (data.groups || []).map(g => ({
            id: g.label, title: g.label, threadCount: g.threadCount, updatedUtc: g.lastReceivedUtc, subjects: [],
          }))
        : (data.groups || [])
      commit('SET_ENTRY_GROUPS', groups)
    } catch {
      commit('SET_ENTRY_GROUPS', [])
    } finally {
      commit('SET_ENTRY_LOADING', false)
    }
    // The sender-cluster read (Email Workspace) has no label-group analog.
    if (!labelKind) await dispatch('fetchGroups')
  },

  /**
   * Opening one group out of that listing: the middle becomes its threads, in the same rows every
   * other entry lists, and the crumb gains the group's name.
   */
  async openBriefingGroup({ commit, state: s }, group) {
    if (!group?.id) return
    const labelKind = s.entryGroupsKind === 'labelGroups'
    commit('SET_OPEN_THREAD', null)
    commit('SET_OPEN_ENTRY_GROUP', group)
    commit('SET_ENTRY_LOADING', true)
    try {
      const params = labelKind
        ? { entry: 'labelGroups', label: group.id }
        : { entry: 'groups', groupId: group.id }
      commit('SET_ENTRY_REQUEST', params)
      const { rows, total } = await readBriefingThreads(params, 0)
      commit('SET_ENTRY_THREADS', { threads: rows, total })
    } catch {
      commit('SET_ENTRY_REQUEST', null)
      // An archived or vanished group answers 404 — the listing is where the user came from, and it
      // is where they are put back, rather than being left in front of an empty group.
      commit('SET_OPEN_ENTRY_GROUP', null)
      commit('SET_ENTRY_THREADS', { threads: [], total: 0 })
    } finally {
      commit('SET_ENTRY_LOADING', false)
    }
  },

  /** Back out of one group to the listing it was opened from. */
  closeBriefingGroup({ commit, state: s, dispatch }) {
    if (!s.openEntryGroup) return
    commit('SET_OPEN_ENTRY_GROUP', null)
    commit('SET_OPEN_THREAD', null)
    commit('SET_ENTRY_THREADS', { threads: [], total: 0 })
    commit('SET_ENTRY_REQUEST', null)
    return dispatch('loadEntryGroups')
  },

  /**
   * A group changed (made, archived, split, merged) — the Briefing has to agree.
   *
   * Both halves, because both are wrong the moment one does: the COUNT on the strip ("14 email
   * groups") and the LISTING under it. Archiving is the case that matters most — an archived group
   * drops out of the entry and out of the number, which is the acceptance criterion of #24.
   */
  async refreshGroupsEntry({ dispatch, rootGetters }) {
    dispatch('loadBriefing')
    if (rootGetters['briefing/emailCategory'] === 'groups') await dispatch('loadEntryGroups')
  },

  async loadEntryThreads({ commit }, entry) {
    if (!entry || entry === 'groups') return
    commit('SET_ENTRY_LOADING', true)
    // The user's zone travels with every page, so "today" is their day on page two as on page one.
    const params = { entry, timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone }
    commit('SET_ENTRY_REQUEST', params)
    try {
      const { rows, total } = await readBriefingThreads(params, 0)
      commit('SET_ENTRY_THREADS', { threads: rows, total })
    } catch {
      commit('SET_ENTRY_REQUEST', null)
      commit('SET_ENTRY_THREADS', { threads: [], total: 0 })
    } finally {
      commit('SET_ENTRY_LOADING', false)
    }
  },

  /**
   * The open list's next page, appended ("Show more" / the end of the list scrolled into view) —
   * an entry's threads or one group's. Asked from where the rows in hand end, with the same request
   * that read them; a page that lands after the user moved to another list is dropped.
   */
  async loadMoreEntryThreads({ commit, state: s }) {
    const params = s.entryRequest
    if (!params || s.entryLoading || s.entryLoadingMore || !hasMore(s.entryThreads, s.entryTotal)) return
    commit('SET_ENTRY_LOADING_MORE', true)
    try {
      const before = s.entryThreads.length
      const { rows, total } = await readBriefingThreads(params, before)
      if (s.entryRequest !== params) return
      commit('APPEND_ENTRY_THREADS', { threads: rows, total })
      // A page that added nothing is the end of the list, whatever the total says (rows the index
      // could not name): without this the footer, still in view, would ask again forever.
      if (s.entryThreads.length === before) commit('APPEND_ENTRY_THREADS', { threads: [], total: before })
    } catch {
      // nothing: the rows on screen stand, and "Show more" can be pressed again
    } finally {
      commit('SET_ENTRY_LOADING_MORE', false)
    }
  },

  /**
   * Ticking a thread in that list — the SAME Work-board pick a group's email makes (`emailPick`),
   * so archiving and "Create ongoing" go on reading one selection however it was made. The row's
   * own id is the thread's timeline id, which is what the estate refers to a conversation by.
   */
  toggleThread({ dispatch }, row) {
    if (!row?.id) return
    // The pick's "title" is the group a mail came from in the workspace; from the Briefing there is
    // no group, so the people on the thread are what names it on the rail.
    dispatch('workboard/toggle', emailPick({ id: row.id, subject: row.subject }, { name: row.sender }), { root: true })
  },

  /** Opening one — the crumb gains its subject, and the caller navigates to the thread. */
  openThread({ commit }, row) {
    if (!row?.id) return
    commit('SET_OPEN_THREAD', { id: row.id, subject: row.subject || '' })
  },

  /** …and closing it: back out of the thread, the entry's list still where it was. */
  closeThread({ commit }) { commit('SET_OPEN_THREAD', null) },

  /** Mark the open thread or sender group as opened FROM the matches list (busy-my-web#608). */
  markOpenedFrom({ commit }, v) { commit('SET_OPENED_FROM', v && v.from ? { from: String(v.from), what: String(v.what || 'thread') } : null) },

  // ── The attachment viewer (busy-my-web#481, 2026-09-15 parity audit D-4) ──────────────────────
  //
  // Four actions and no fetching: the bytes are the renderers' business (they download by document
  // id through the one path every file surface shares) and the DELETE is the listing's, so all this
  // owns is which file is open and how it is being looked at.

  /** Open a file in the middle. Anything without a document id is ignored — see `attachmentViewerFile`. */
  openAttachment({ commit }, source) {
    const file = attachmentViewerFile(source)
    if (!file) return
    commit('SET_OPEN_ATTACHMENT', file)
  },

  /** …and closing it, which leaves the listing underneath exactly where it was. */
  closeAttachment({ commit }) { commit('SET_OPEN_ATTACHMENT', null) },

  /** One press of the toolbar's zoom pair. `direction` is 'in' or 'out'. */
  zoomAttachment({ commit, state: s }, direction) {
    const zoom = direction === 'out' ? zoomOut(s.attachmentView.zoom) : zoomIn(s.attachmentView.zoom)
    commit('SET_ATTACHMENT_VIEW', { zoom })
  },

  /** …and of its rotate button: a quarter turn clockwise. */
  rotateAttachment({ commit, state: s }) {
    commit('SET_ATTACHMENT_VIEW', { rotation: rotateRight(s.attachmentView.rotation) })
  },

  /**
   * DELETE the file the viewer is showing (the toolbar's 🗑, frames 40011255:261043 onwards).
   *
   * `DELETE v1/document/{documentId}` — the BFF's own per-document delete, which has existed as
   * long as the documents endpoint has; no new call, exactly as this issue's backend note says.
   *
   * The viewer closes FIRST and the list is re-read after, in that order: the file the middle is
   * drawing is about to stop existing, and a viewer left open over a deleted document would try to
   * fetch bytes that are gone. A failed call leaves everything alone and returns false, so the
   * caller can say so rather than the row vanishing from a listing the server still has.
   */
  async deleteAttachment({ commit, dispatch, state: s, rootGetters }, file = null) {
    const target = file || s.openAttachment
    const id = String(target?.id || '').trim()
    if (!id) return false
    try {
      await api.delete('document', id)
    } catch {
      return false
    }
    if (s.openAttachment?.id === id) commit('SET_OPEN_ATTACHMENT', null)
    // The file may also be ticked onto the Work-board; a pick standing for a document that no
    // longer exists is a selection that cannot be acted on.
    const pick = (rootGetters['workboard/items'] || []).find(i => i?.kind === 'attachment-pick' && String(i.attachmentId) === id)
    if (pick) dispatch('workboard/remove', pick.id, { root: true })
    // …and the listing and the counts are both now one file out of date.
    const scope = rootGetters['attachments/list']?.scope || 'home'
    dispatch('attachments/fetchList', { scope, className: rootGetters['attachments/className'] }, { root: true })
    dispatch('attachments/ensureCounts', { scope, force: true }, { root: true })
    return true
  },

  // ── The waiting verbs, from the Briefing (busy-my-web#293) ────────────────────────────────────
  //
  // The waiting object itself (`waiting/*`) owns the calls; these two actions exist because ending a
  // wait changes what the BRIEFING says, and only the session knows that. Nothing here re-implements
  // a verb: they are the store's own `waiting/resolve` and `waiting/remind` with the entry's list
  // and its count kept honest afterwards.

  /**
   * "No longer waiting": the wait is closed, the row leaves the list, and the dashboard's "waiting
   * for replies" falls with it.
   *
   * Dropped locally rather than re-read. The count comes from the classification store and the list
   * from this one, so a re-fetch the instant after a write shows the number the user just changed —
   * the row would blink back. The next poll (60s) reconciles both from the server.
   */
  async resolveWaiting({ commit, dispatch, state: s }, { waitingId, timelineId } = {}) {
    if (!waitingId || !timelineId) return null
    const resolved = await dispatch('waiting/resolve', { id: waitingId, by: 'user-cleared' }, { root: true })
    const left = (s.entryThreads || []).filter(t => (t?.timelineId || t?.id) !== timelineId)
    commit('SET_ENTRY_THREADS', { threads: left, total: Math.max(0, (Number(s.entryTotal) || 0) - 1) })
    const counts = countsAfterWaitResolved(s.briefingCounts)
    if (counts) commit('SET_BRIEFING_COUNTS', { status: s.briefingStatus, counts })
    return resolved
  },

  /**
   * "Remind me — tomorrow / in 3 days / next week." A reminder never moves the expected date (being
   * chased about a wait and the wait itself are two different times), so the row stays exactly where
   * it is and only its `remindAtUtc` changes.
   */
  async remindWaiting({ commit, dispatch, state: s }, { waitingId, timelineId, remindAtUtc } = {}) {
    if (!waitingId || !remindAtUtc) return null
    const at = remindAtUtc?.toISOString?.() ?? remindAtUtc
    const row = await dispatch('waiting/remind', { id: waitingId, remindAtUtc: at }, { root: true })
    // The list holds its own copy of the waiting fields (they arrived on the Briefing row), so the
    // one that changed is written back — otherwise the strip would go on offering to set a reminder
    // that is already set until the next fetch.
    const threads = (s.entryThreads || []).map(t => ((t?.timelineId || t?.id) === timelineId && t?.waiting
      ? { ...t, waiting: { ...t.waiting, remindAtUtc: at } }
      : t))
    commit('SET_ENTRY_THREADS', { threads, total: s.entryTotal })
    return row
  },

  /** "Follow up": the WORDS for a nudge. The compose itself is the reply flow that already exists. */
  followUpWaiting({ dispatch }, waitingId) {
    if (!waitingId) return Promise.resolve(null)
    return dispatch('waiting/followUp', waitingId, { root: true })
  },

  /** The header's "Back" — out of the group, still in the session. */
  backToGroups({ commit, state: s }) {
    if (s.openedFrom?.what === 'group') commit('SET_OPENED_FROM', null)
    commit('SET_OPEN_SENDER', null)
    commit('SET_GROUP_EMAILS', [])
    // …and the same on the way back OUT of a group (busy-my-web#322): the header's Back means the
    // group listing, which it cannot be while a conversation still holds the middle.
    commit('SET_OPEN_THREAD', null)
    commit('SET_ARMED', null)
  },

  // ── THE CATEGORY LAYER (2026-09-15 parity audit F1-1 / busy-main-bff#38) ─────────────────────

  /**
   * The category summary for the sender groups (`POST v1/email/groups/summary`).
   *
   * Asked once when the session's groups are first listed and again after an archive — the strip
   * RE-COUNTS, and a category the archive emptied disappears from it (the server omits zero
   * headings). A refusal leaves whatever the strip already had: the categories were true when they
   * arrived, and a strip that empties itself on one bad call is worse than one a minute old.
   */
  async loadCategorySummary({ commit, state: s }, { minCount = 2, take = 50 } = {}) {
    if (s.categoriesStatus === 'loading') return null
    commit('SET_CATEGORIES_STATUS', 'loading')
    try {
      const res = await api.insert('email/groups/summary', summaryRequest({ minCount, take }))
      const summary = readSummary(res)
      commit('SET_CATEGORIES', { ...summary, status: 'ready' })
      return summary
    } catch {
      commit('SET_CATEGORIES_STATUS', 'error')
      return null
    }
  },

  /** "View ↗" on a category row — the middle narrows to that category's groups (40008255:426104). */
  openCategoryListing({ commit }, category) {
    commit('SET_OPEN_CATEGORY', category)
    commit('SET_OPEN_SENDER', null)
    commit('SET_OPEN_THREAD', null)
    commit('SET_ARMED', null)
  },

  /** "Back" out of a category sub-listing, to the full group listing. */
  closeCategoryListing({ commit }) {
    commit('SET_OPEN_CATEGORY', '')
    commit('SET_ARMED', null)
  },

  /**
   * A category SUGGESTION ("Create ongoing Health groups 762 emails"): it pre-ticks the senders the
   * server resolved for it and arms the flow that already exists. No new write endpoint is involved
   * — this is the archive / create the workspace has always had, aimed by the server's grouping.
   */
  async armCategorySuggestion({ commit, dispatch, rootGetters }, row) {
    if (!row?.arms || !(row.senders || []).length) return false
    const groups = rootGetters['briefing/emailGroups'] || []
    const wanted = new Set(row.senders.map(x => String(x).toLowerCase()))
    for (const g of groups) {
      if (g && wanted.has(String(g.sender || '').toLowerCase())) {
        dispatch('workboard/add', groupPick(g), { root: true })
      }
    }
    commit('SET_ARMED', row.arms)
    return true
  },

  // ── SUBJECT FAMILIES — "5 Threads" (F1-3 / busy-main-bff#39) ─────────────────────────────────

  /**
   * The open group's mail clustered by normalised subject (`POST v1/email/groups/threads`).
   *
   * Read when a group opens, so the group's own rows can carry their "LinkedIn 12" family count
   * without a second request, and so the "N Threads" row in the strip knows its number.
   */
  async loadFamilies({ commit, state: s }, group) {
    const target = group || null
    if (!target?.sender) return null
    if (s.familiesStatus === 'loading') return null
    commit('SET_FAMILIES_STATUS', 'loading')
    try {
      const res = await api.insert('email/groups/threads', familiesRequest(target))
      const families = readFamilies(res)
      commit('SET_FAMILIES', families)
      commit('SET_FAMILIES_STATUS', 'ready')
      return families
    } catch {
      commit('SET_FAMILIES', [])
      commit('SET_FAMILIES_STATUS', 'error')
      return null
    }
  },

  /** "5 Threads" — the families listing becomes the middle (frame 40010619:262299). */
  openFamilies({ commit }) {
    commit('SET_FAMILIES_OPEN', true)
    commit('SET_OPEN_THREAD', null)
    commit('SET_ARMED', null)
  },

  /** "Back" out of the families listing, to the group's own conversations. */
  closeFamilies({ commit }) {
    commit('SET_FAMILIES_OPEN', false)
    commit('SET_ARMED', null)
  },

  /** Ticking a family — ONE pick standing for its N conversations (lib/emailThreadFamilies). */
  toggleFamily({ dispatch, getters: g }, family) {
    if (!family?.key) return
    dispatch('workboard/toggle', familyPick(family, g.openGroup), { root: true })
  },

  // ── "POSSIBLE ONGOING MATCHES" (F1-4 / busy-main-bff#40) ─────────────────────────────────────

  /**
   * "Add" on an armed "Add to ongoing" — the ranked panel opens IN the box (frames 40010646:340618
   * → 341095), rather than the middle becoming the whole "Ongoing Workspace" listing at once. The
   * listing is still where this ends: it is the panel's SEARCH step.
   *
   * The ranking is the BFF's (`POST v1/ongoing/suggest-from-threads` → `existing[]`), asked about
   * whatever the selection stands for: the ticked threads and families by timeline id, the ticked
   * groups (and an open group) by sender.
   */
  async openMatchesPanel({ commit, dispatch, getters: g, rootGetters }) {
    const items = rootGetters['workboard/items'] || []
    const timelineIds = [...new Set([...(g.threadIds || []), ...familyTimelineIds(items)])]
    const picks = sessionPicks(items)
    const senders = picks.filter(i => i.kind === 'email-group').map(i => i.sender).filter(Boolean)
    if (!timelineIds.length && !senders.length && g.openGroup?.sender) senders.push(g.openGroup.sender)
    if (!timelineIds.length && !senders.length) return false
    // ── The matches are the MIDDLE now (busy-my-web#523, frame 40010646:341095) ──
    // Fatemeh's redesign: pressing Add no longer opens a panel of radio cards inside the box — the
    // middle becomes the "Possible ongoing matches" listing, which is the page-level picker with
    // the ranking on top of it. So the picker is opened FIRST and the ranking is fetched behind it;
    // the list is drawn from the user's own Ongoings either way, so there is nothing to wait for.
    const opened = await dispatch('openPickerForSelection')
    if (!opened) return false
    commit('SET_MATCHES_PANEL', { open: true, rows: [], status: 'loading' })
    // What the selection can be RECOGNISED by — the evidence half of #526's rule. The senders are
    // the groups', the subject terms are whatever the ticked rows are called (a group's name, a
    // thread's subject, a family's), which is the only text about the selection this action holds.
    const selection = { senders, subjects: picks.map(i => i.title).filter(Boolean) }
    try {
      const res = await api.insert('ongoing/suggest-from-threads', matchesRequest({ timelineIds, senders, take: MATCH_TABLE_TAKE }))
      // #526 (Saffet): only candidates that clear the confidence cut-off AND share evidence with the
      // selection are presented as matches. Nothing else stands in — `defaultMatchRows`'s fallback
      // rows are what put the fish and the bread on the screen, and the listing under the matches
      // already shows every Ongoing the user has, unmarked, which is what he asked for instead.
      const rows = matchRows(confidentMatches(readExisting(res), selection), MATCH_TABLE_TAKE)
      commit('SET_MATCHES_PANEL_ROWS', { rows, status: 'ready' })
      return rows
    } catch {
      commit('SET_MATCHES_PANEL_ROWS', { rows: [], status: 'error' })
      return []
    }
  },

  closeMatchesPanel({ commit }) { commit('SET_MATCHES_PANEL', { open: false }) },
  pickMatch({ commit }, id) { commit('SET_MATCHES_PANEL_PICKED', id) },
  setMatchQuery({ commit }, q) { commit('SET_MATCHES_PANEL_QUERY', q) },

  /**
   * "Search" — the typed query, ranked by the server (`GET v1/ongoing/search?q=`), and then the
   * page-level picker opens ON that query (frame 40010912:367754). The picker is the same one the
   * group and thread flows have always used, so wherever the user asked from, one listing answers.
   */
  async searchMatches({ commit, dispatch, state: sState }) {
    const query = String(sState.matchesPanelQuery || '').trim()
    if (!query || sState.matchesPanelSearching) return false
    commit('SET_MATCHES_PANEL_SEARCHING', true)
    try {
      const res = await api.get('ongoing/search', { q: query, take: 10 })
      commit('SET_MATCHES_PANEL_ROWS', { rows: matchRows(readExisting(res), 10), status: 'ready' })
      commit('SET_PICKER_QUERY', query)
      await dispatch('openPickerForSelection')
      commit('SET_MATCHES_PANEL', { open: false })
      // The picker's own "Connect" links are the commit now (frame 40010912:368184): nothing stays
      // armed, so the chooser and its "Add" pill stand down and the box goes back to "Ask anything".
      // The SELECTION stays — it is what Connect files.
      commit('SET_ARMED', null)
      return true
    } catch {
      commit('SET_MATCHES_PANEL_ROWS', { rows: [], status: 'error' })
      return false
    } finally {
      commit('SET_MATCHES_PANEL_SEARCHING', false)
    }
  },

  /**
   * "Add" with a match picked: the panel's row IS an Ongoing, so this is the picker's own commit
   * (`connectPickedOngoing`) — the same request, the same receipt, the same green band.
   */
  async commitMatchesPanel({ commit, dispatch, state: sState }) {
    if (sState.matchesPanelQuery.trim()) return dispatch('searchMatches')
    const picked = sState.matchesPanelRows.find(r => r.id === sState.matchesPanelPicked)
    if (!picked) return false
    // The picker holds WHICH threads are being filed; open it for the current selection first so
    // `connectPickedOngoing` has them, exactly as pressing Connect on a listing row would.
    const opened = await dispatch('openPickerForSelection')
    if (!opened) return false
    const result = await dispatch('connectPickedOngoing', { id: picked.id, title: picked.title })
    commit('SET_MATCHES_PANEL', { open: false })
    commit('SET_ARMED', null)
    return result
  },

  /** Choosing a suggestion arms it (the frame's filled radio); choosing it again disarms. */
  // Arming "Add to ongoing" used to open the matches step at once (Pouria, 2026-09-16), because the
  // step was three small radios inside the box and could sit under the chooser. Since the redesign
  // it is the whole MIDDLE (busy-my-web#523): frame 40010646:340618 draws "Add to ongoing" SELECTED
  // with the chooser still standing and the listing nowhere to be seen, and 341095 — the listing —
  // is what the next frame, after Add is pressed, shows. So arming only arms now; `commitThread`
  // opens the listing. Un-arming still puts the listing away, whichever press opened it.
  arm({ commit, dispatch, state: s }, id) {
    const next = s.armed === id ? null : id
    commit('SET_ARMED', next)
    if (!dispatch) return
    // Anything else armed (or nothing at all) closes it: the listing is about ONE verb, and it
    // cannot stand over a selection that is now being archived.
    if (s.matchesPanelOpen && !(next && addsOnCommit(next))) dispatch('closeOngoingPicker')
  },

  dismissNotice({ commit }) { commit('DISMISS_NOTICE') },

  /**
   * Commit the armed suggestion — today that is only ever an archive, through the BFF's real
   * `email/archiveSenders` (busy-main-bff#7 → acc-account-sync-srv `v1/email/archiveSenders`).
   */
  async commitArmed({ state: s, getters: g, commit, rootGetters, dispatch }) {
    const groups = rootGetters['briefing/emailGroups'] || []
    const items = rootGetters['workboard/items'] || []
    // "Add" on the open email's own create (busy-my-web#517): the box opens the form, filled from
    // the mail — the session only says which form and which thread. The option disarms: the chooser
    // has done its job the moment the form is up.
    const createMenu = threadCreateMenu(s.armed)
    if (createMenu) {
      commit('SET_ARMED', null)
      window.dispatchEvent(new CustomEvent(THREAD_CREATE_EVENT, { detail: { menu: createMenu, thread: s.openThread } }))
      return
    }
    // A DELETE asks first (acc-account-sync-srv#18): the CTA opens the confirm strip, and the
    // strip's own Delete is what calls the endpoint. Checked before the archive because the two
    // commit paths are otherwise identical and only one of them is destructive.
    if (deletesOnCommit(s.armed)) {
      const senders = deleteTargets(s.armed, { openGroup: g.openGroup, items, groups })
      commit('SET_DELETE_CONFIRM', deleteConfirmation(senders, groups))
      return
    }
    if (!archivesOnCommit(s.armed)) return
    const senders = archiveTargets(s.armed, { openGroup: g.openGroup, items, groups })
    await dispatch('archiveSenders', senders)
  },

  // ── Delete selected groups (acc-account-sync-srv#18) ──────────────────────────────────────────

  /** "Cancel" on the confirm strip: the option stays armed, nothing is sent. */
  cancelDeleteSenders({ commit }) { commit('SET_DELETE_CONFIRM', null) },

  /** "Delete" on the confirm strip — the one press that actually sends it. */
  async confirmDeleteSenders({ commit, dispatch, state: s }) {
    const asked = s.deleteConfirm
    const senders = asked?.senders || []
    commit('SET_DELETE_CONFIRM', null)
    // …or the thread selection's own big delete (busy-my-web#516), which rides the same strip.
    // It deletes the ids the question COUNTED, not whatever is ticked by the time Delete is pressed —
    // the number on the strip is the number that goes.
    if (asked?.kind === 'threads') return dispatch('bulkThreads', { action: 'delete', ids: asked.ids })
    if (!senders.length) return
    await dispatch('deleteSenders', senders)
  },

  // ── Create an ongoing (busy-my-web#104) ───────────────────────────────────────────────────────

  /**
   * "Next" on the armed "Create ongoing" suggestion → the naming step (frame 6). The field opens
   * with the one honest default there is (a single group's own name) and is left alone on the way
   * back, so stepping out and in again does not throw away what was typed.
   */
  startCreate({ commit, getters: g, state: s }) {
    // busy-my-web#275 arms the same step from the thread offer. The mode decides two things and only
    // two: what the step is ABOUT (threads or groups) and what the commit sends. The panel, the CTA
    // and the name field are one flow either way.
    const fromThreads = s.armed === 'thread-ongoing'
    if (s.armed !== 'create-ongoing' && !fromThreads) return
    // The MODE is set first, because `createTargets` reads it: asking that getter before the mode
    // was put back would answer about the create that is not happening. (It was the other way round,
    // and that was a bug: after one thread-create the mode was still 'threads', so the GROUP create
    // found no targets and its step never opened again.) Groups are the default, so only a change is
    // committed: arming the thread offer, or coming back to the group create after one.
    if (fromThreads) commit('SET_CREATE_MODE', 'threads')
    else if (s.createMode === 'threads') commit('SET_CREATE_MODE', 'groups')
    // The thread targets are read directly rather than through `createTargets`, which is the same
    // list by then — one reader, so the step cannot list one thing while the commit sends another.
    const targets = fromThreads ? g.threadCreateTargets : g.createTargets
    if (!targets.length) return
    // The AI's proposal is the starting text, and it is a PROPOSAL: the field is editable and the
    // user's own typing is never overwritten by a later offer (hence `if (!s.ongoingName)`).
    if (!s.ongoingName) {
      commit('SET_ONGOING_NAME', fromThreads
        ? (s.threadSuggestion?.title || '')
        : suggestedOngoingName(targets))
    }
    commit('SET_CREATE_STAGE', 'naming')
  },

  // ── The thread offer (busy-my-web#275) ────────────────────────────────────────────────────────

  /**
   * The board changed — ask about the selection, eventually. Debounced and de-duplicated by the
   * runner: a selection still being made is not asked about, and the same selection is asked about
   * once. A selection that falls under two threads clears the offer immediately, so the row goes as
   * soon as it stops being true rather than when an answer comes back.
   */
  refreshThreadSuggestion({ commit, dispatch, getters: g, state: s }) {
    const ids = g.threadPicks
    const scheduled = threadRunner(dispatch).schedule(ids)
    if (ids.length < 2) {
      if (s.threadSuggestion || s.threadSuggestionState !== 'idle') {
        commit('SET_THREAD_SUGGESTION', { suggestion: null, signature: '', status: 'idle' })
      }
      return
    }
    // The selection moved on: drop the old offer rather than showing it against a new set.
    if (scheduled && scheduled !== s.threadSignature && s.threadSuggestion) {
      commit('SET_THREAD_SUGGESTION', { suggestion: null, signature: '', status: 'loading' })
    }
  },

  /**
   * The call itself. A failure is SILENT — state 'error', no banner: nobody asked for this offer, so
   * nothing about it is worth interrupting the user with.
   */
  async fetchThreadSuggestion({ commit, getters: g }, { ids, signature } = {}) {
    const list = ids || []
    if (list.length < 2) return null
    commit('SET_THREAD_SUGGESTION', { status: 'loading' })
    try {
      const res = await api.insert('ongoing/suggest-from-threads', suggestRequest(list))
      // The board may have moved while this was in flight; an answer about a selection that is no
      // longer on screen is not shown.
      if (selectionSignature(g.threadPicks) !== signature) return null
      const suggestion = readSuggestion(res)
      commit('SET_THREAD_SUGGESTION', { suggestion, signature, status: 'ready' })
      return suggestion
    } catch {
      commit('SET_THREAD_SUGGESTION', { suggestion: null, signature: '', status: 'error' })
      return null
    }
  },

  /**
   * "Add to <existing story>" — the join half of the offer. The threads are linked to a story that
   * already exists, so nothing is created and the session stays open; the receipt says what happened
   * and repeats busy-timeline-srv#21's promise about future mail.
   *
   * Two ways to link, tried in order: the bulk `ongoing/{id}/threads` where the deployment has it,
   * and the per-thread link every story page already uses where it does not (see `joinPlan`).
   */
  async joinExistingOngoing({ commit, dispatch, getters: g, state: s }) {
    const join = s.threadSuggestion?.join
    const plan = joinPlan(join, g.threadCreateTargets)
    if (!plan || s.joining) return null
    commit('SET_JOINING', true)
    try {
      try {
        await api.insert(plan.bulk.url, plan.bulk.body)
      } catch (e) {
        // No bulk endpoint on this deployment (404/405) — link them one at a time. Any other failure
        // is a real one and is not retried thread by thread.
        const status = e?.response?.status
        if (status !== 404 && status !== 405) throw e
        for (const call of plan.fallback) await api.insert(call.url, call.body)
      }
      commit('SET_JOIN_NOTICE', linkedNotice(plan.timelineIds.length, join.title))
      commit('SET_THREAD_SUGGESTION', { suggestion: null, signature: '', status: 'idle' })
      resetThreadSuggestionRunner()
      // The threads have a home now, so they are no longer something to have selected.
      for (const target of g.threadCreateTargets) dispatch('workboard/remove', target.id, { root: true })
      commit('SET_ARMED', null)
      // The story gained threads; its page and the sidebar list should say so.
      dispatch('ongoing/fetch', null, { root: true })
      return join.ongoingId
    } catch {
      commit('SET_JOIN_NOTICE', null)
      return null
    } finally {
      commit('SET_JOINING', false)
    }
  },

  dismissJoinNotice({ commit }) { commit('SET_JOIN_NOTICE', null) },

  setOngoingName({ commit }, name) { commit('SET_ONGOING_NAME', name) },

  /** The panel's back arrow: out of the naming step, still armed on the suggestion it came from. */
  backFromCreate({ commit }) { commit('SET_CREATE_STAGE', null) },

  /** The panel's close: out of the step AND off the suggestion — the selection itself stays. */
  // The mode is deliberately NOT reset here (busy-my-web#275): it is set on the way IN, by
  // `startCreate`, which is the only place that knows which create is being opened. Resetting on the
  // way out would be a second opinion about the same fact, and a wrong one for the back arrow, which
  // keeps the step armed to come straight back to.
  closeCreate({ commit }) {
    commit('SET_CREATE_STAGE', null)
    commit('SET_ARMED', null)
  },

  /**
   * The unlink "×" on a group inside the create step (frame 7). It is the SAME removal the
   * Work-board's own × does — one list, so the counts on both fall together — and unlinking the
   * last group drops back to the suggestion list, which has something to offer again.
   */
  /**
   * The unlink "×" on a THREAD inside the create step (busy-my-web#275) — the same removal the
   * Work-board's own × does, so the counts fall together, and dropping the last one steps back out
   * of the create exactly as unlinking the last group does.
   */
  unlinkThread({ commit, dispatch, getters: g }, id) {
    if (!id) return
    dispatch('workboard/remove', id, { root: true })
    if (g.threadCreateTargets.filter(t => t.id !== id).length) return
    commit('SET_CREATE_STAGE', null)
    commit('SET_ARMED', null)
  },

  unlinkGroup({ commit, dispatch, getters: g, rootGetters }, sender) {
    if (!sender) return
    dispatch('workboard/remove', groupPickId(sender), { root: true })
    // Unlinking the OPEN group is not a board removal — that group is a target because it is open,
    // not because it was ticked (`createTargets` falls back to it when nothing is). Recomputing
    // against the still-open group made its × a dead click: the panel never shrank and never
    // dropped back to the choices, unlike unlinking the last real pick. So the group being unlinked
    // is taken out of the recompute whichever way it got there. (The group stays OPEN in the middle
    // — the user is still reading it; what ends is its place in the create.)
    const unlinkedOpenGroup = !!g.openGroup && (g.openGroup.sender === sender || groupName(g.openGroup) === sender)
    const left = createTargets({
      openGroup: unlinkedOpenGroup ? null : g.openGroup,
      items: (rootGetters['workboard/items'] || []).filter(i => i.id !== groupPickId(sender)),
      groups: rootGetters['briefing/emailGroups'] || [],
    })
    if (!left.length) {
      commit('SET_CREATE_STAGE', null)
      commit('SET_ARMED', null)
    }
  },

  /**
   * Create it (frames 8-9). The session closes on success — the board hands back, the picks go and
   * the story is the subject now — but the "Ongoing created" banner is kept, and the created story
   * is returned so the caller can open it.
   */
  async createOngoing({ commit, dispatch, getters: g, state: s }, { path = '' } = {}) {
    // Two memberships, one flow (busy-my-web#275): the picked GROUPS become follows and their mail
    // is linked, or the picked THREADS become the story and nobody is followed at all. Which one is
    // decided when the step opens (`startCreate`), never here.
    const fromThreads = s.createMode === 'threads'
    const targets = fromThreads ? g.threadCreateTargets : g.createTargets
    const name = s.ongoingName
    const ready = fromThreads ? canCreateFromThreads(name, targets) : canCreate(name, targets)
    if (!ready || s.createState === 'creating') return null
    commit('SET_CREATE', { status: 'creating', ongoing: null })
    try {
      // The "Creating ongoing, <name>" band stays on screen for a perceivable beat before the
      // redirect takes the page (busy-my-web#498, 2026-09-15 parity audit).
      const res = await withMinimumBeat(fromThreads
        ? api.insert('ongoing/from-threads', threadsRequest(name, targets))
        : api.insert('ongoing/from-email-groups', createRequest(name, targets)))
      const story = res?.data?.data ?? res?.data ?? null
      if (!story?.id) throw new Error('no story')
      // Seed the story so its page renders instantly, and put it in the sidebar's list.
      commit('ongoing/SET_CURRENT_STORY', story, { root: true })
      commit('ongoing/SET_RECENT', story.id, { root: true })
      dispatch('ongoing/fetch', null, { root: true })
      commit('SET_CREATE', { status: 'created', ongoing: story })
      // …and the Work-board is EMPTIED first (2026-09-15 parity audit F2-4, busy-my-web#498): the
      // picks have become the story's own membership, and leaving them ticked let a faded ghost of
      // the selection survive the redirect onto the new story's page.
      dispatch('clearSelection')
      dispatch('close', { path, keepNotice: true })
      return story
    } catch (e) {
      // A guest has no mailbox, so this is all but unreachable — but the quota answer is the same
      // one every other create honours rather than reporting as a failure.
      if (e?.response?.status === 403 && e?.response?.data?.code === 'GUEST_LIMIT') {
        commit('SET_CREATE', { status: 'idle', ongoing: null })
        loginForSave()
        return null
      }
      commit('SET_CREATE', { status: 'error', ongoing: null })
      return null
    }
  },

  dismissCreateNotice({ commit }) { commit('DISMISS_CREATE_NOTICE') },

  // ── The Work-board's thread actions (busy-my-web#274) ─────────────────────────────────────────
  //
  // Every one of these takes the SAME list — the timeline ids of what is ticked, read off the board
  // by the `threadIds` getter — because that is the whole point of the board: one selection, many
  // verbs. None of them takes a copy of it, so a verb can never act on a stale pick.

  dismissThreadNotice({ commit }) { commit('DISMISS_THREAD_NOTICE') },
  setOngoingQuery({ commit }, q) { commit('SET_ONGOING_QUERY', q) },
  setGroupName({ commit }, name) { commit('SET_GROUP_NAME', name) },
  /** The panel an armed option opened — closing it leaves the option armed and the picks alone. */
  closeThreadPanel({ commit }) {
    commit('SET_THREAD_PANEL', null)
    commit('SET_PANEL_THREADS', null)
  },

  /**
   * Open the ongoing chooser about a specific set of threads — a GROUP row's own "Add to ongoing".
   * The board's picks are left exactly as they are: the user pointed at a row, which is not a
   * decision to change what is ticked.
   */
  openOngoingChooserFor({ commit }, timelineIds) {
    if (!(timelineIds || []).length) return
    commit('SET_PANEL_THREADS', timelineIds)
    commit('SET_THREAD_PANEL', 'ongoing')
  },

  /**
   * ADD A WHOLE GROUP TO AN ONGOING THAT ALREADY EXISTS (busy-my-web#370).
   *
   * The new flow the updated section asks for, and it is the OLD one with one step in front of it.
   * A sender-group row on the workspace listing carries `sender`, a name, a count and a stamp — and
   * no timeline ids at all, because the listing is derived by sender and never reads the mail. So
   * this opens the group first (the one existing read, `openGroup`), takes the ids of what came
   * back, and hands them to the chooser the Work-board has offered since busy-my-web#274. From
   * there it is unchanged: search your ongoings, pick one, `addThreadsToOngoing` files the lot in a
   * single request and the receipt quotes the server's counts.
   *
   * Nothing is invented and nothing is duplicated: had this dispatched its own request, the story
   * would have gained a different kind of step depending on which surface the user was standing on.
   *
   * A group with no mail in it opens as a group and offers no chooser — there is nothing to file,
   * and an empty chooser is a question with no answer behind it.
   */
  async addGroupToOngoing({ dispatch, getters: g }, group) {
    if (!group?.sender) return false
    await dispatch('openGroup', group)
    const ids = groupTimelineIds(g.groupEmails)
    if (!ids.length) return false
    // busy-my-web#421 moved WHERE the question is asked, and nothing else: the frames turn the
    // middle into the "Ongoing Workspace" listing instead of unfolding the 260px chooser, so this
    // hands the very same ids to the very same commit (`addThreadsToOngoing`) through the picker.
    // The group's name comes along because the frame's notice quotes it — "Linkedin group is
    // connecting to Job-offer ongoing…" — which a bare list of thread ids could not say.
    dispatch('openOngoingPicker', { timelineIds: ids, label: groupName(group) })
    return true
  },

  // ── The page-level Ongoing picker (busy-my-web#421) ──────────────────────────────────────────
  //
  // Every surface that asks "which Ongoing?" from the workspace opens THIS: the group listing's
  // "Add to ongoing", the matched-groups chooser's "Connect to another Ongoing" (#422), the open
  // group's "Add to Ongoing" (#422) and the attachments' (#423). One picker, one commit, one story
  // step — the coordination the three issues each asked for in their own notes.
  //
  // The inline chooser (`openOngoingChooserFor`) is deliberately untouched: the frames still use it
  // for a single thread row in the Briefing strip, where there is no middle to take over.

  /**
   * Show the picker in the middle, about a specific set of threads.
   *
   * The board's picks are left exactly as they are — the user pointed at something, which is not a
   * decision to change what is ticked — and the inline chooser is closed, because two choosers open
   * at once would be two answers to one question.
   */
  openOngoingPicker({ commit, dispatch }, { timelineIds = [], label = '' } = {}) {
    const ids = (timelineIds || []).filter(Boolean)
    if (!ids.length) return false
    commit('SET_PANEL_THREADS', ids)
    commit('SET_THREAD_PANEL', null)
    commit('SET_PICKER', { open: true, label })
    // The listing is the user's own stories; ask for them rather than drawing whatever the last
    // page happened to leave in the module.
    dispatch('ongoing/fetch', null, { root: true })
    return true
  },

  setOngoingPickerQuery({ commit }, q) { commit('SET_PICKER_QUERY', q) },

  /**
   * "Search" on the picker (busy-my-web#535, frames "search ongoings 2 → 4"): the composer's text,
   * ranked by the server (`GET v1/ongoing/search?q=`), becomes the middle's "<query> result" table.
   * The composer is emptied for the next query ("Continue search ongoings..."). A failed call still
   * answers — from the user's own list, by the same title rule the inline chooser uses — because a
   * search box that says nothing back is worse than a local answer.
   */
  async searchOngoings({ commit, rootGetters, state: s }) {
    const query = String(s.pickerQuery || '').trim()
    if (!s.pickerOpen || !query || s.pickerSearchStatus === 'searching') return false
    commit('SET_PICKER_SEARCH', { query: s.pickerSearchQuery, rows: s.pickerSearchRows, status: 'searching' })
    let rows
    let status = 'ready'
    try {
      const res = await api.get('ongoing/search', { q: query, take: 10 })
      rows = matchRows(readExisting(res), 10)
    } catch {
      rows = ongoingPickerRows(rootGetters['ongoing/items'] || [], query).map(r => ({ id: r.id, title: r.title }))
      status = 'error'
    }
    // Closed while it ran: nothing to answer.
    if (!s.pickerOpen) return false
    commit('SET_PICKER_SEARCH', { query, rows, status })
    commit('SET_PICKER_QUERY', '')
    return true
  },

  /** "Clear selections" / the query crumb's ✕ on the picker: back to the matches table. */
  clearOngoingSearch({ commit }) {
    commit('SET_PICKER_QUERY', '')
    commit('SET_PICKER_SEARCH', {})
  },

  /** "Not related" on a match row (busy-my-web#535). Session-only: there is no endpoint to record it. */
  dismissPickerMatch({ commit }, id) { if (id) commit('DROP_MATCH_ROW', String(id)) },

  // ── What a selection of ATTACHMENTS commits (busy-my-web#423, frames 40011000:380706 →
  //    40011008:402606) ────────────────────────────────────────────────────────────────────────
  //
  // Four options, all four live: the per-document download every file surface uses, the page-level
  // picker (#421), the one create both ongoing paths go through (`createOngoingWithThreads`), and
  // — since 2026-09-15 (parity audit D-5) — "Save to Files" on the BFF's new copy-into-Files proxy
  // (see SAVE_TO_FILES_PATH).

  /**
   * "Download all" — the picked files, one at a time.
   *
   * NOT `downloadAllAttachment`: that endpoint zips ONE email's attachments
   * (`document/download/email/{id}/all`) and a selection made in this listing is files off many
   * different emails, so asking it would download the wrong set. Each file goes through the same
   * `document/download/{id}` the row's own download button uses. One failure is swallowed per file:
   * a file the server cannot hand over must not stop the other nine.
   */
  async downloadPickedAttachments({ commit, rootGetters }) {
    const picks = attachmentPicks(rootGetters['workboard/items'] || [])
    if (!picks.length) return null
    commit('SET_THREAD_RUNNING', true)
    let saved = 0
    try {
      for (const pick of picks) {
        try {
          const res = await api.downloadDocument(pick.attachmentId)
          if (saveBlob(pick.title || 'attachment', res?.data)) saved += 1
        } catch { /* the next file is still worth trying */ }
      }
      commit('SET_THREAD_NOTICE', saved
        ? { kind: 'done', text: `${saved} file${saved === 1 ? '' : 's'} downloaded` }
        : { kind: 'error', text: 'Could not download those files.' })
      commit('SET_ARMED', null)
      return saved
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /**
   * "Add to ongoing" — the page-level picker (#421), over the CONVERSATIONS the files arrived on.
   *
   * A document belongs to a thread, and a thread is what every ongoing membership endpoint takes;
   * filing the mail is also what keeps the file reachable from the story afterwards. Files the
   * payload gives no `timelineId` for cannot be filed at all — they are dropped rather than sent as
   * blanks, and a selection made entirely of them opens nothing (see the PR note).
   */
  openPickerForAttachments({ dispatch, rootGetters }) {
    const ids = attachmentTimelineIds(rootGetters['workboard/items'] || [])
    if (!ids.length) return false
    return dispatch('openOngoingPicker', { timelineIds: ids, label: '' })
  },

  /** "Create ongoing" — #104's flow with the files' conversations as its first members. */
  createOngoingFromAttachments({ dispatch, rootGetters }, { path = '' } = {}) {
    const items = rootGetters['workboard/items'] || []
    const ids = attachmentTimelineIds(items)
    if (!ids.length) return Promise.resolve(null)
    // The story is named after what the user actually picked: the first file. A story called
    // "Emails" is what every other create falls back to and says nothing about these ones.
    const title = attachmentPicks(items)[0]?.title || ''
    return dispatch('createOngoingWithThreads', { title, timelineIds: ids, path })
  },

  /**
   * "Save to Files" (stage 5) — copy the picked documents into the user's own Files area.
   *
   * The whole step is here: the request, the frames' green banner with its Organize and Undo, and
   * the rail entry naming the files. The CALL landed on 2026-09-15 (parity audit D-5), so the step
   * runs — `savedCount` is what the banner counts, and a payload the server could only copy in part
   * still lands its banner for what it did save (the `failures` it names are not a failed save).
   */
  async saveAttachmentsToFiles({ commit, rootGetters }) {
    const items = rootGetters['workboard/items'] || []
    const ids = attachmentDocumentIds(items)
    if (!ids.length) return null
    const files = attachmentPicks(items).map(p => ({ id: p.attachmentId, name: p.title }))
    // The copies are filed against the conversation the files came in on — but only when the
    // selection IS one conversation. Files picked off five different emails have no single thread
    // to belong to, and naming one of them would file the other four in the wrong place.
    const threads = attachmentTimelineIds(items)
    commit('SET_THREAD_RUNNING', true)
    try {
      const res = await api.insert(SAVE_TO_FILES_PATH, {
        documentIds: ids,
        timelineId: threads.length === 1 ? threads[0] : null,
      })
      // What the server actually copied. A partial copy is still a save: the banner, the rail entry
      // and the Undo are all about the files that landed, so the ones that did not are left out of
      // every one of them rather than offering an Undo for a copy that was never made.
      // Undo takes the SOURCE ids, so what is remembered is "what was asked for, minus what the
      // server named as a failure" — never the copies' own ids, which are new documents.
      const failed = new Set((res?.data?.failures || []).map(f => String(f?.documentId ?? '')).filter(Boolean))
      const savedIds = ids.filter(id => !failed.has(String(id)))
      const count = Number.isFinite(res?.data?.savedCount) ? res.data.savedCount : savedIds.length
      if (!count) {
        commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not save those files.' })
        return null
      }
      commit('SET_SAVED_FILES', savedIds)
      commit('ADD_RECEIPT', savedToFilesReceipt({ files, at: new Date() }))
      commit('SET_THREAD_NOTICE', savedToFilesNotice())
      commit('SET_ARMED', null)
      return count
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not save those files.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /** The banner's Undo: take the copies back out again, and stop offering to do it twice. */
  async undoSaveToFiles({ commit, state: s }) {
    const ids = [...(s.savedFiles || [])]
    if (!ids.length) return null
    try {
      await api.insert(`${SAVE_TO_FILES_PATH}/undo`, { documentIds: ids })
      commit('SET_SAVED_FILES', [])
      commit('SET_THREAD_NOTICE', { kind: 'done', text: 'Undone' })
      return ids.length
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not undo that.' })
      return null
    }
  },

  /**
   * "Connect to another Ongoing" (busy-my-web#422) — the picker, about whatever is ticked.
   *
   * Threads carry their own timeline ids, so a selection of threads goes straight to the picker.
   * SENDER groups carry none — the listing is derived by sender and never reads the mail — so each
   * ticked group is opened first, exactly as `addGroupToOngoing` does for one, and the ids of what
   * came back are pooled. One picker, one commit, however many groups were ticked.
   */
  async openPickerForSelection({ dispatch, getters: g, rootGetters }) {
    const threads = g.threadIds
    if (threads.length) {
      return dispatch('openOngoingPicker', { timelineIds: threads, label: '' })
    }
    const items = rootGetters['workboard/items'] || []
    const groups = rootGetters['briefing/emailGroups'] || []
    const picked = sessionPicks(items).filter(i => i.kind === 'email-group')
    const ids = []
    const names = []
    for (const pick of picked) {
      const group = groups.find(x => x && x.sender === pick.sender) || null
      if (!group) continue
      await dispatch('openGroup', group)
      ids.push(...groupTimelineIds(g.groupEmails))
      names.push(groupName(group))
    }
    if (!ids.length) return false
    return dispatch('openOngoingPicker', { timelineIds: [...new Set(ids)], label: names.join(', ') })
  },

  /**
   * "Connect to suggested Ongoing(s)" (busy-my-web#422, frames 40010766:357940 → 40010788:358603).
   *
   * One dispatch per EDGE, exactly as the listing's own Connect and the match cards do it
   * (`ongoing/addSuggestedThread`): a card standing for four emails is four Suggested edges, and
   * accepting the offer is accepting all of them. Nothing new is asked of the server and no second
   * grouping is invented — the offers are the same `ongoing/suggestions` every surface reads.
   *
   * The answered picks come off the board afterwards, for the reason every other bulk verb drops
   * its picks: the question they were ticked to answer has been answered.
   */
  async connectSuggestedGroups({ commit, dispatch, rootGetters }) {
    const items = rootGetters['workboard/items'] || []
    const cards = matchCards(rootGetters['ongoing/suggestions'] || [])
    const matched = matchedGroupPicks(items, cards)
    if (!matched.length) return null
    const names = matchedGroupNames(matched)
    const ids = matchedRelationshipIds(matched)
    commit('SET_THREAD_RUNNING', true)
    commit('SET_THREAD_NOTICE', connectingMatchNotice(names))
    try {
      // Per edge, and swallowed per edge: one bad id must not leave the whole selection unanswered.
      await withMinimumBeat(
        Promise.all(ids.map(id => dispatch('ongoing/addSuggestedThread', id, { root: true }).catch(() => false))),
      )
      const title = matchChipLabel(matched[0].match)
      // The green band's "View" opens the story it landed in (busy-my-web#496, frame 40010788:358603).
      const ongoingId = matched[0]?.match?.ongoingId || matched[0]?.match?.id || ''
      commit('SET_THREAD_NOTICE', connectedMatchNotice(names, title, ongoingId))
      // …and the rail gets the receipt the picker path has always written (busy-my-web#496,
      // frame 40010930:373966): "HMRC email group / Connected to the ongoing / … / Health issues ·
      // ongoing". Without it a connection made from the chooser left the rail silent.
      commit('ADD_RECEIPT', matchReceipt({ names, ongoingTitle: title, at: new Date() }))
      for (const m of matched) dispatch('workboard/remove', m.pick.id, { root: true })
      commit('SET_ARMED', null)
      dispatch('ongoing/fetch', null, { root: true })
      return matched.length
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /** "Dismiss suggested match(es)" — the same edges, refused (`ongoing/rejectSuggestedThread`). */
  async dismissSuggestedGroups({ commit, dispatch, rootGetters }) {
    const items = rootGetters['workboard/items'] || []
    const cards = matchCards(rootGetters['ongoing/suggestions'] || [])
    const matched = matchedGroupPicks(items, cards)
    if (!matched.length) return null
    const ids = matchedRelationshipIds(matched)
    commit('SET_THREAD_RUNNING', true)
    try {
      await Promise.all(ids.map(id => dispatch('ongoing/rejectSuggestedThread', id, { root: true }).catch(() => false)))
      commit('SET_THREAD_NOTICE', dismissedMatchNotice(matched.length))
      commit('SET_ARMED', null)
      return matched.length
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /** Leave the picker. The armed option stays armed and the picks stay picked. */
  closeOngoingPicker({ commit }) {
    commit('SET_PICKER', { open: false })
    commit('SET_PANEL_THREADS', null)
    // …and the matches step goes with it (busy-my-web#523). The two used to be separate levels of
    // the walk — the box's cards, then the listing — and Back had to be pressed twice to get out of
    // one screen. Since the redesign the ranking has no surface of its own: it is the top of THIS
    // list, so leaving the list is leaving it, and Back from here returns to the group.
    commit('SET_MATCHES_PANEL', { open: false })
  },

  /**
   * "Connect" on one of the picker's rows.
   *
   * The commit is `addThreadsToOngoing` — busy-my-web#274's, unchanged: one request for the whole
   * set, the server's counts in the answer. What this adds is the frames' own two sentences around
   * it ("… is connecting to …", then "… connected to …") and the rail entry they leave behind,
   * which the inline chooser never drew because the inline chooser is not a page.
   */
  async connectPickedOngoing({ commit, dispatch, getters: g, state: s }, ongoing) {
    const id = ongoing?.id || ongoing
    if (!id || s.pickerConnecting) return null
    // Read before the commit: `addThreadsToOngoing` clears the panel's ids when it lands, and a
    // receipt written afterwards would then claim nothing was filed.
    const count = g.panelThreadIds.length
    const title = ongoing?.title || ''
    commit('SET_PICKER_CONNECTING', id)
    commit('SET_THREAD_NOTICE', connectingNotice(s.pickerLabel, title))
    const result = await withMinimumBeat(dispatch('addThreadsToOngoing', ongoing))
    commit('SET_PICKER_CONNECTING', null)
    // A failure leaves the picker open with its own error banner (addThreadsToOngoing sets it), so
    // the user can try another story rather than being returned to a listing with no explanation.
    if (!result) return null
    commit('ADD_RECEIPT', connectReceipt({ label: s.pickerLabel, ongoingTitle: title, count, at: new Date() }))
    commit('SET_THREAD_NOTICE', connectedNotice(s.pickerLabel, title, id))
    dispatch('closeOngoingPicker')
    return result
  },
  clearSummary({ commit }) { commit('SET_SUMMARY', null) },

  /**
   * Keep a summary (the spec's "Save-as-note" on the Briefing callout).
   *
   * Filed against the FIRST thread it read, so the note is reachable from the conversation it is
   * about rather than floating in the Notes list with no context. Written with the same escaping and
   * the same `api.insert('note')` the research save uses — a summary is a paragraph of somebody
   * else's prose, and putting it into an HTML body unescaped is how a note becomes markup.
   */
  async saveSummaryAsNote({ commit, state: s }) {
    const summary = s.summary
    if (!summary) return null
    const lines = [summary.summary, ...(summary.points || []).map(p => `• ${p}`)].filter(Boolean)
    const content = lines.map(line => `<p>${noteEscape(line)}</p>`).join('')
    try {
      const res = await api.insert('note', {
        title: 'Email summary',
        content,
        isImportant: false,
        isPreset: false,
        timelineId: summary.perThread?.[0]?.timelineId || null,
      })
      commit('SET_SUMMARY', null)
      commit('SET_THREAD_NOTICE', { kind: 'done', text: 'Summary saved as a note' })
      return res?.data?.data ?? res?.data ?? null
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not save the note.' })
      return null
    }
  },
  clearTaskDraft({ commit }) { commit('SET_TASK_DRAFT', null) },

  /**
   * Create the task the user has just READ and edited (busy-my-web#274).
   *
   * The second half of the draft flow: `createTaskDraft` above asks the server what the threads say
   * and shows it; this is the person agreeing to it. It goes back through the same endpoint with
   * `draftOnly:false` and the edited fields, rather than posting to `task` directly, so that the
   * server still owns the linking — filing the thread into the story is its business, and a client
   * that made the task itself would have to re-implement that too.
   */
  async commitTaskDraft({ commit, getters: g, state: s }, edits = {}) {
    const draft = s.taskDraft
    const ids = g.threadIds.length ? g.threadIds : (draft?.timelineIds || [])
    if (!draft || !ids.length) return null
    commit('SET_THREAD_RUNNING', true)
    try {
      const res = await api.insert('email/threads/create-task', {
        timelineIds: ids,
        draftOnly: false,
        ongoingId: edits.ongoingId || null,
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      const created = res?.data?.data ?? res?.data ?? null
      commit('SET_TASK_DRAFT', null)
      commit('SET_ARMED', null)
      commit('SET_THREAD_NOTICE', { kind: 'done', text: `Task created, ${created?.title || draft.title}` })
      return created
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not create the task.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /**
   * Commit the armed THREAD option. Returns whatever the verb produced (a story, a summary, a
   * draft) or null, so a component can navigate on the strength of it.
   *
   * The panel-opening options are handled here too rather than in the component: which option opens
   * a panel is a fact about the option, and the component would otherwise have to keep a second
   * list of them in step with `opensPanel`.
   */
  async commitThread({ commit, dispatch, getters: g }, { path = '' } = {}) {
    const armed = g.armed
    if (!armed || g.threadRunning) return null
    // ── "Add to ongoing" → the "Possible Ongoing matches" panel (2026-09-15 parity audit F1-4) ──
    // Every door that used to swap the middle for the whole listing (or unfold the 260px chooser)
    // now goes through the ranked panel first, whichever level of the workspace it was asked from.
    // With the panel already open the same press COMMITS it — the picked match, or the typed search.
    if (addsOnCommit(armed)) {
      return g.matchesPanelOpen ? dispatch('commitMatchesPanel') : dispatch('openMatchesPanel')
    }
    if (armed === 'threads-add-ongoing') { commit('SET_THREAD_PANEL', 'ongoing'); return null }
    if (armed === 'threads-create-group') { commit('SET_THREAD_PANEL', 'group'); return null }
    // ── The matched groups' four options (busy-my-web#422) ──
    // Two of them are doors: "Connect to another Ongoing" and the open group's "Add to Ongoing"
    // both open the page-level picker (#421), one about the ticked groups' threads and one about
    // the open group's. The fourth option is `create-ongoing`, which is #104's flow and never
    // reaches here.
    if (armed === 'matches-connect-another') return dispatch('openPickerForSelection')
    // `group-add-ongoing` is handled by the matches-panel branch above since the 2026-09-15 parity
    // audit (F1-4) — `addGroupToOngoing` is still the picker's own door, dispatched from the panel's
    // Search step and from the listing row's own action.
    if (armed === 'matches-connect') return dispatch('connectSuggestedGroups')
    if (armed === 'matches-dismiss') return dispatch('dismissSuggestedGroups')
    // ── What a selection of FILES commits (busy-my-web#423) ──
    if (armed === 'attachments-add-ongoing') return dispatch('openPickerForAttachments')
    if (armed === 'attachments-download') return dispatch('downloadPickedAttachments')
    if (armed === 'attachments-save-files') return dispatch('saveAttachmentsToFiles')
    if (armed === 'attachments-create-ongoing') return dispatch('createOngoingFromAttachments', { path })

    const verb = bulkVerb(armed)
    // A big "Delete conversations" asks first, by its exact count (busy-my-web#516): the same
    // confirm the group delete raises — strip, Cancel / Delete in the action row — and its Delete
    // (`confirmDeleteSenders`) is what sends it. A handful still goes straight to Trash.
    if (verb === 'delete') {
      const confirm = threadsBinConfirmation(g.threadIds, { confirm: TEXT.deleteCta, cancel: TEXT.deleteConfirmCancel })
      if (confirm) { commit('SET_DELETE_CONFIRM', confirm); return null }
    }
    if (verb) return dispatch('bulkThreads', verb)
    if (armed === 'threads-mark-important') return dispatch('markImportantThreads')
    if (armed === 'threads-summarise') return dispatch('summariseThreads')
    if (armed === 'threads-create-task') return dispatch('createTaskDraft')
    if (armed === 'threads-export') return dispatch('exportThreads')
    if (armed === 'threads-create-ongoing') return dispatch('createOngoingFromThreads', { path })
    return null
  },

  /** The mailbox listing saying how big its page and its folder are (see `state.listing`). */
  setListing({ commit }, v) { commit('SET_LISTING', v) },

  /**
   * "Mark as important all" (the mailbox listing's chooser, frame 40009853:294742). The bulk route
   * has no such verb (archive / delete / markRead / markUnread only), so this is the timeline
   * store's own per-thread mark, over the ticked ids — the same call the toolbar's star made.
   * The picks stay: important mail is still mail one can act on.
   */
  async markImportantThreads({ commit, dispatch, getters: g }) {
    const ids = g.threadIds
    if (!ids.length) return null
    commit('SET_THREAD_RUNNING', true)
    commit('SET_THREAD_NOTICE', { kind: 'running', text: 'Working on the selected threads...' })
    try {
      await dispatch('timeline/markAsImportant', ids, { root: true })
      commit('SET_THREAD_NOTICE', { kind: 'done', text: `Marked ${ids.length} ${ids.length === 1 ? 'conversation' : 'conversations'} as important` })
      commit('SET_ARMED', null)
      return true
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'That did not work. Try again.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /**
   * Archive / delete / mark read / mark unread, over the ticked threads.
   *
   * The picks are dropped afterwards for the same reason the archive drops its senders: whatever was
   * just archived or deleted is no longer something to have selected, and a board still holding it
   * invites the next verb to act on mail that has moved.
   */
  async bulkThreads({ commit, dispatch, getters: g, rootGetters }, arg) {
    // A verb, or `{ action, ids }` from a confirm strip that already counted its ids (#516).
    const action = typeof arg === 'object' && arg ? arg.action : arg
    const ids = typeof arg === 'object' && arg?.ids?.length ? arg.ids : g.threadIds
    if (!ids.length || !action) return null
    commit('SET_THREAD_RUNNING', true)
    commit('SET_THREAD_NOTICE', { kind: 'running', text: 'Working on the selected threads...' })
    try {
      const res = await api.insert('email/threads/bulk', bulkRequest(ids, action))
      const result = res?.data?.data ?? res?.data ?? {}
      const receipt = bulkReceipt({ action, timelineIds: ids, result, at: new Date() })
      commit('ADD_RECEIPT', receipt)
      commit('SET_THREAD_NOTICE', { kind: 'done', text: receipt.title })
      // Mark read/unread leaves the mail exactly where it is, so the selection is still meaningful;
      // archive and delete move it out from under the board, so those picks go.
      if (action === 'archive' || action === 'delete') {
        for (const item of (rootGetters['workboard/items'] || [])) {
          if (item?.kind === 'email-pick' && ids.includes(item.emailId)) {
            dispatch('workboard/remove', item.id, { root: true })
          }
        }
        commit('SET_ARMED', null)
      }
      return result
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'That did not work. Try again.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /**
   * Read the ticked threads. The answer lands in the Briefing as a callout with its sources.
   *
   * A failure is still an ANSWER here: the BFF replies 200 with a deterministic reading flagged
   * `isFallback`, so the only thing this has to handle is the request itself falling over.
   */
  async summariseThreads({ commit, getters: g }) {
    const ids = g.threadIds
    if (!ids.length) return null
    commit('SET_THREAD_RUNNING', true)
    commit('SET_SUMMARY', null)
    try {
      const res = await api.insert('email/threads/summarise', {
        timelineIds: ids,
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      const summary = res?.data?.data ?? res?.data ?? null
      commit('SET_SUMMARY', summary)
      return summary
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not summarise those threads.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /**
   * The task the ticked threads imply — as a DRAFT.
   *
   * `draftOnly` is deliberate and is the decision this action exists to make: the fields come back
   * and the task composer opens prefilled, so the person who will own the task is the person who
   * presses create. An AI that quietly adds items to someone's list is worse than one that misses
   * them, because the user cannot remember agreeing to any of it.
   */
  async createTaskDraft({ commit, getters: g }) {
    const ids = g.threadIds
    if (!ids.length) return null
    commit('SET_THREAD_RUNNING', true)
    try {
      const res = await api.insert('email/threads/create-task', {
        timelineIds: ids,
        draftOnly: true,
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      const draft = res?.data?.data ?? res?.data ?? null
      commit('SET_TASK_DRAFT', draft)
      return draft
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not read those threads.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /** Download the ticked threads as a JSON document. */
  async exportThreads({ commit, getters: g }) {
    const ids = g.threadIds
    if (!ids.length) return null
    commit('SET_THREAD_RUNNING', true)
    try {
      const res = await api.insert('email/threads/export', { timelineIds: ids })
      const bundle = res?.data?.data ?? res?.data ?? { threads: [] }
      const saved = saveJson(exportFilename(new Date()), exportDocument(bundle, new Date()))
      commit('SET_THREAD_NOTICE', saved
        ? { kind: 'done', text: `Exported ${ids.length} thread${ids.length === 1 ? '' : 's'}` }
        : { kind: 'error', text: 'Could not save the export.' })
      return bundle
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not export those threads.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /**
   * A brand-new Ongoing out of the ticked threads.
   *
   * NOT `ongoing/from-email-groups`: that route follows SENDERS, and a thread pick has none — it is
   * the conversation the user chose, not everyone who ever wrote from that address. `POST v1/ongoing`
   * takes timelineIds and links exactly them, which is what "create an ongoing from these threads"
   * means. The story is titled from the first thread's subject, the one honest default there is.
   */
  async createOngoingFromThreads({ dispatch, getters: g, rootGetters }, { path = '' } = {}) {
    const ids = g.threadIds
    if (!ids.length) return null
    const first = (rootGetters['workboard/items'] || [])
      .find(i => i?.kind === 'email-pick' && i.emailId === ids[0])
    const title = String(first?.subject || first?.title || '').trim() || 'Emails'
    return dispatch('createOngoingWithThreads', { title, timelineIds: ids, path })
  },

  /** A user email group becomes a story: its threads are the members, its title is the story's. */
  createOngoingFromGroup({ dispatch }, { title = '', timelineIds = [], path = '' } = {}) {
    if (!timelineIds.length) return null
    return dispatch('createOngoingWithThreads', { title, timelineIds, path })
  },

  /**
   * The one create both paths run through, so a story made from a selection and one made from a
   * group are the same story with the same members and the same aftermath.
   */
  async createOngoingWithThreads({ commit, dispatch }, { title = '', timelineIds = [], path = '' } = {}) {
    const ids = (timelineIds || []).filter(Boolean)
    if (!ids.length) return null
    const name = String(title || '').trim() || 'Emails'
    commit('SET_THREAD_RUNNING', true)
    try {
      const res = await api.insert('ongoing', { title: name, origin: 'email', timelineIds: ids })
      const story = res?.data?.data ?? res?.data ?? null
      if (!story?.id) throw new Error('no story')
      commit('ongoing/SET_CURRENT_STORY', story, { root: true })
      commit('ongoing/SET_RECENT', story.id, { root: true })
      dispatch('ongoing/fetch', null, { root: true })
      commit('SET_CREATE', { status: 'created', ongoing: story })
      // …and the Work-board is EMPTIED first (2026-09-15 parity audit F2-4, busy-my-web#498): the
      // picks have become the story's own membership, and leaving them ticked let a faded ghost of
      // the selection survive the redirect onto the new story's page.
      dispatch('clearSelection')
      dispatch('close', { path, keepNotice: true })
      return story
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not create the ongoing.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /**
   * File the ticked threads into an Ongoing the user already has.
   *
   * One request for the whole selection, not one per thread: the story gains ONE step saying what
   * the user did instead of N copies of "Linked by the user", and a failure cannot leave it half
   * updated (busy-timeline-srv's LinkThreads). The confirmation quotes the SERVER's counts, because
   * a selection overlapping what the story already holds is the ordinary case.
   */
  async addThreadsToOngoing({ commit, dispatch, getters: g, rootGetters }, ongoing) {
    const ids = g.panelThreadIds
    const ongoingId = ongoing?.id || ongoing
    if (!ids.length || !ongoingId) return null
    commit('SET_THREAD_RUNNING', true)
    try {
      const res = await api.insertWithPath('ongoing', `${ongoingId}/threads`, { timelineIds: ids })
      const result = res?.data?.data ?? res?.data ?? {}
      const title = ongoing?.title
        || (rootGetters['ongoing/items'] || []).find(o => o?.id === ongoingId)?.title
        || ''
      commit('SET_THREAD_NOTICE', addedToOngoingNotice(result, title))
      commit('SET_THREAD_PANEL', null)
      commit('SET_PANEL_THREADS', null)
      commit('SET_ARMED', null)
      // The story's own list orders on its last change, and it just changed.
      dispatch('ongoing/fetch', null, { root: true })
      return result
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not add those threads.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  // ── The user's own email groups (busy-my-web#274) ─────────────────────────────────────────────

  /** Read the caller's groups. Failure is an empty list, not a broken panel. */
  async fetchGroups({ commit }, { includeArchived = false } = {}) {
    commit('SET_GROUPS_LOADING', true)
    try {
      const res = await api.get('email/groups/user', { includeArchived })
      commit('SET_GROUPS', res?.data?.data ?? res?.data ?? [])
    } catch {
      commit('SET_GROUPS', [])
    } finally {
      commit('SET_GROUPS_LOADING', false)
    }
  },

  /** Make a group out of the ticked threads. The picks STAY: filing them is not un-choosing them. */
  async createGroup({ commit, dispatch, getters: g, state: s }, title) {
    const ids = g.threadIds
    const name = String(title ?? s.groupName ?? '').trim()
    if (!ids.length || !name) return null
    commit('SET_THREAD_RUNNING', true)
    try {
      const res = await api.insert('email/groups/user', createGroupRequest(name, ids))
      const group = res?.data?.data ?? res?.data ?? null
      if (!group?.id) throw new Error('no group')
      commit('UPSERT_GROUP', group)
      commit('SET_THREAD_NOTICE', { kind: 'done', text: `Email group created, ${group.title}` })
      commit('SET_THREAD_PANEL', null)
      commit('SET_GROUP_NAME', '')
      commit('SET_ARMED', null)
      // The Briefing's "N email groups" is now one more, and its listing one row longer.
      dispatch('refreshGroupsEntry')
      return group
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not create the group.' })
      return null
    } finally {
      commit('SET_THREAD_RUNNING', false)
    }
  },

  /** Add the ticked threads to a group that already exists. */
  async addThreadsToGroup({ commit, getters: g }, groupId) {
    const ids = g.threadIds
    if (!ids.length || !groupId) return null
    try {
      const res = await api.insertWithPath('email/groups/user', `${groupId}/threads`, { timelineIds: ids })
      const group = res?.data?.data ?? res?.data ?? null
      if (group?.id) commit('UPSERT_GROUP', group)
      return group
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not add those threads.' })
      return null
    }
  },

  /**
   * Hide a group, or bring it back. Nothing at all happens to its threads — archiving a COLLECTION
   * and archiving the MAIL in it are two acts, and the second one is the bulk verb above.
   */
  async archiveGroup({ commit, dispatch, state: s }, { groupId, archived = true } = {}) {
    if (!groupId) return null
    try {
      const res = await api.insertWithPath('email/groups/user', `${groupId}/archive?archived=${archived}`, {})
      const group = res?.data?.data ?? res?.data ?? null
      if (group?.id) commit('UPSERT_GROUP', group)
      // …and it leaves the Briefing with it (busy-main-bff#24): the listing drops it on the spot so
      // the row goes when the button is pressed, and the count is re-read because it changed too.
      if (archived) {
        commit('SET_ENTRY_GROUPS', withoutGroup(s.entryGroups, groupId))
        if (s.openEntryGroup?.id === groupId) commit('SET_OPEN_ENTRY_GROUP', null)
      }
      dispatch('refreshGroupsEntry')
      return group
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not archive the group.' })
      return null
    }
  },

  /** Split a group: the picked threads leave for a new one, the rest stay. Both come back. */
  async splitGroup({ commit, dispatch }, { groupId, timelineIds = [], newTitle = '', sourceTitle = '' } = {}) {
    if (!groupId || !timelineIds.length) return null
    try {
      const res = await api.insertWithPath('email/groups/user', `${groupId}/split`, {
        timelineIds,
        newTitle: splitTitle(newTitle, sourceTitle),
      })
      const result = res?.data?.data ?? res?.data ?? null
      if (result?.source?.id) commit('UPSERT_GROUP', result.source)
      if (result?.created?.id) commit('UPSERT_GROUP', result.created)
      commit('SET_THREAD_NOTICE', { kind: 'done', text: `Split into ${result?.created?.title || 'a new group'}` })
      // One group became two: both the count and the listing moved.
      dispatch('refreshGroupsEntry')
      return result
    } catch {
      // The server refuses a split that would empty a side, which the panel already prevents —
      // reaching this means something else went wrong, so re-read rather than guess at the state.
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not split the group.' })
      dispatch('fetchGroups')
      return null
    }
  },

  /** Fold one group into another. The target survives; the source is archived, never deleted. */
  async mergeGroup({ commit, dispatch }, { groupId, intoGroupId } = {}) {
    if (!groupId || !intoGroupId || groupId === intoGroupId) return null
    try {
      const res = await api.insertWithPath('email/groups/user', `${groupId}/merge`, { intoGroupId })
      const group = res?.data?.data ?? res?.data ?? null
      if (group?.id) commit('UPSERT_GROUP', group)
      // The SOURCE changed too (it is archived now) and this answer does not carry it, so the list
      // is re-read rather than left showing a group that has just been folded away.
      await dispatch('fetchGroups')
      commit('SET_THREAD_NOTICE', { kind: 'done', text: `Merged into ${group?.title || 'the target group'}` })
      // The source is archived now, so it leaves the Briefing's count and listing with the merge.
      dispatch('refreshGroupsEntry')
      return group
    } catch {
      commit('SET_THREAD_NOTICE', { kind: 'error', text: 'Could not merge the groups.' })
      return null
    }
  },

  async archiveSenders({ commit, dispatch, rootGetters, state: s }, senders) {
    const list = (senders || []).filter(Boolean)
    if (!list.length || s.archiveState === 'archiving') return
    const groups = rootGetters['briefing/emailGroups'] || []
    const picked = new Set(list)
    const requested = groups
      .filter(x => x && picked.has(x.sender))
      .reduce((sum, x) => sum + (Number(x.totalCount) || 0), 0)
    commit('SET_ARCHIVE', { status: 'archiving', requested, result: null })
    try {
      const res = await withMinimumBeat(api.insert('email/archiveSenders', archiveRequest(list)))
      const result = res?.data?.data ?? res?.data ?? {}
      // The receipt is written from the groups as they WERE — they are about to leave the list —
      // and names the CATEGORY where every archived group came from one (busy-my-web#494/#38).
      commit('ADD_RECEIPT', archiveReceipt({
        groups,
        senders: list,
        result,
        at: new Date(),
        category: categoryReceiptName(categoryOfSenders(list, groups, s.categories)),
      }))
      // Dropped here rather than re-fetched: the provider is still catching up when this returns.
      dispatch('briefing/setEmailGroups', withoutSenders(groups, list), { root: true })
      // Whatever was archived is no longer something to have selected.
      for (const sender of list) dispatch('workboard/remove', groupPickId(sender), { root: true })
      commit('SET_ARMED', null)
      commit('SET_ARCHIVE', { status: 'archived', result })
      if (s.openSender && picked.has(s.openSender)) dispatch('backToGroups')
      // …and the category strip RE-COUNTS (2026-09-15 parity audit F1-1, frame 40008242:422388):
      // a category the archive emptied disappears from it.
      if (s.categoriesStatus === 'ready') dispatch('loadCategorySummary')
      // Archiving changes every count on the dashboard, not just the groups list — re-read them
      // rather than letting the poll show numbers that include mail the user just filed away.
      dispatch('loadBriefing')
      // The summary was written about groups that included these senders — re-ask.
      await dispatch('briefing/ensureEmails', {
        groups: rootGetters['briefing/emailGroups'] || [],
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        force: true,
      }, { root: true })
    } catch {
      commit('SET_ARCHIVE', { status: 'error', result: null })
    }
  },

  /**
   * DELETE the mail of the given senders (acc-account-sync-srv#18 → `POST v1/email/deleteSenders`).
   *
   * The archive's twin, and deliberately written as one: the same guard against a double press, the
   * same "count them as they WERE, because they are about to leave the list" receipt, the same
   * optimistic drop from the Briefing, the same re-reads afterwards. Two differences, and both are
   * about the verb:
   *
   *   • it is only ever reached through the confirm strip (`confirmDeleteSenders`);
   *   • the receipt carries an UNDO, holding the senders, because `restoreSenders` takes the very
   *     same body. That is the whole reason the delete is safe to offer at all.
   *
   * `deleted` is the count the receipt quotes — never the count that was asked for — so the band
   * cannot claim to have deleted mail the provider had already dropped.
   */
  async deleteSenders({ commit, dispatch, rootGetters, state: s }, senders) {
    const list = (senders || []).filter(Boolean)
    if (!list.length || s.deleteState === 'deleting') return
    const groups = rootGetters['briefing/emailGroups'] || []
    const picked = new Set(list)
    const requested = groups
      .filter(x => x && picked.has(x.sender))
      .reduce((sum, x) => sum + (Number(x.totalCount) || 0), 0)
    commit('SET_DELETE', { status: 'deleting', requested, result: null })
    try {
      const res = await withMinimumBeat(api.insert('email/deleteSenders', deleteRequest(list, s.mainFolder)))
      const result = res?.data?.data ?? res?.data ?? {}
      commit('ADD_RECEIPT', deleteReceipt({
        groups,
        senders: list,
        result,
        at: new Date(),
        category: categoryReceiptName(categoryOfSenders(list, groups, s.categories)),
      }))
      dispatch('briefing/setEmailGroups', withoutSenders(groups, list), { root: true })
      for (const sender of list) dispatch('workboard/remove', groupPickId(sender), { root: true })
      commit('SET_ARMED', null)
      commit('SET_DELETE', { status: 'deleted', result })
      if (s.openSender && picked.has(s.openSender)) dispatch('backToGroups')
      if (s.categoriesStatus === 'ready') dispatch('loadCategorySummary')
      dispatch('loadBriefing')
      await dispatch('briefing/ensureEmails', {
        groups: rootGetters['briefing/emailGroups'] || [],
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        force: true,
      }, { root: true })
    } catch {
      commit('SET_DELETE', { status: 'error', result: null })
    }
  },

  /**
   * UNDO (acc-account-sync-srv#18 → `POST v1/email/restoreSenders`, the same body).
   *
   * Reached from the receipt's Undo and from the band's. The groups it puts back are re-read rather
   * than reconstructed: the optimistic drop above removed them from the Briefing, and a restore
   * that guessed their counts would put back a list that disagrees with the mailbox.
   *
   * DEFENSIVE: a BFF without the route 404s and the band says the restore failed — the delete
   * itself is untouched by that, which is the truth, and the receipt keeps its Undo so it can be
   * pressed again once the route is there.
   */
  async restoreSenders({ commit, dispatch, rootGetters, state: s }, senders) {
    const list = (senders || []).filter(Boolean)
    if (!list.length || s.deleteState === 'restoring') return
    commit('SET_DELETE', { status: 'restoring', requested: s.deleteRequested, result: null })
    try {
      const res = await withMinimumBeat(api.insert('email/restoreSenders', deleteRequest(list, s.mainFolder)))
      const result = res?.data?.data ?? res?.data ?? {}
      commit('SET_DELETE', { status: 'restored', result })
      // The groups are back in the mailbox, so the Briefing is asked again rather than patched.
      dispatch('loadBriefing')
      dispatch('fetchGroups')
      if (s.categoriesStatus === 'ready') dispatch('loadCategorySummary')
      await dispatch('briefing/ensureEmails', {
        groups: rootGetters['briefing/emailGroups'] || [],
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        force: true,
      }, { root: true })
      return result
    } catch {
      commit('SET_DELETE', { status: 'restore-error', result: null })
      return null
    }
  },
}
